export const CHECKOUT_LOCALES = [
  'en',
  'es',
  'fr',
  'de',
  'it',
  'pt',
  'nl',
  'pl',
  'tr',
  'ru',
  'ar',
  'zh',
  'ja',
  'ko',
  'hi',
] as const;

export type CheckoutLocale = (typeof CHECKOUT_LOCALES)[number];

const CHECKOUT_LOCALE_SET = new Set<string>(CHECKOUT_LOCALES);

const LOCALE_ALIASES: Record<string, CheckoutLocale> = {
  JP: 'ja',
  JPY: 'ja',
  JPN: 'ja',
  JAPANESE: 'ja',
  CN: 'zh',
  CHI: 'zh',
  CHINESE: 'zh',
  KR: 'ko',
  KOR: 'ko',
  KOREAN: 'ko',
};

const NUMBER_LOCALES: Record<CheckoutLocale, string> = {
  en: 'en',
  es: 'es',
  fr: 'fr',
  de: 'de',
  it: 'it',
  pt: 'pt-BR',
  nl: 'nl',
  pl: 'pl',
  tr: 'tr',
  ru: 'ru',
  ar: 'ar',
  zh: 'zh-CN',
  ja: 'ja-JP',
  ko: 'ko-KR',
  hi: 'hi-IN',
};

function isCheckoutLocale(value: string): value is CheckoutLocale {
  return CHECKOUT_LOCALE_SET.has(value);
}

/** Checkout locale from `translateLanguage` (merchant language code). Absent/unknown is English. */
export function resolveCheckoutLocale(translateLanguage?: string | null): CheckoutLocale {
  const compact = String(translateLanguage ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s_]+/g, '-');
  if (!compact) return 'en';
  if (LOCALE_ALIASES[compact]) return LOCALE_ALIASES[compact];
  const primary = compact.split('-')[0].toLowerCase();
  return isCheckoutLocale(primary) ? primary : 'en';
}

export function checkoutNumberLocale(locale: CheckoutLocale): string {
  return NUMBER_LOCALES[locale];
}

export function checkoutDir(locale: CheckoutLocale): 'ltr' | 'rtl' {
  return locale === 'ar' ? 'rtl' : 'ltr';
}

export function checkoutBrandClass(locale: CheckoutLocale): string {
  return locale === 'ja' || locale === 'zh' || locale === 'ko' ? ' checkout-brand-cjk' : '';
}

export type CheckoutCopy = {
  secureCheckout: string;
  amountDue: string;
  toMerchant: (name: string) => string;
  paymentMethod: string;
  cardMethod: string;
  cryptoMethod: string;
  alternativePayment: string;
  bankMethod: string;
  chooseBank: string;
  searchBank: string;
  noBanksFound: string;
  bankListUnavailable: string;
  payWithBank: string;
  bankRedirectNote: string;
  footerNote: string;
  cancel: string;
  orderDetails: string;
  orderIdLabel: string;
  amountLabel: string;
  securePayments: string;
  securityCode: string;
  creditCard: string;
  cvvHint: string;
  noOrderDetails: string;
  cardNumber: string;
  cardholderName: string;
  expiryDate: string;
  cvv: string;
  cardNumberPlaceholder: string;
  cardholderPlaceholder: string;
  expiryPlaceholder: string;
  cvvPlaceholder: string;
  otpPlaceholder: string;
  payNow: string;
  payAmount: (amount: string, currency: string) => string;
  processing: string;
  loadingDetails: string;
  linkUnavailable: string;
  missingCheckoutLink: string;
  invalidCheckoutLink: string;
  cardNumberRequired: string;
  cardNumberInvalid: string;
  nameRequired: string;
  required: string;
  expiryFormat: string;
  cvvInvalid: string;
  paymentFailed: string;
  redirectCard: (label: string) => string;
  cardLabel: string;
  paymentLabel: string;
  verifyPayment: string;
  otpHint: string;
  otpLabel: string;
  verifying: string;
  verifyAndPay: string;
  otpFailed: string;
  refresh: string;
  refreshing: string;
  refreshHint: string;
  refreshFailed: string;
  submitted: string;
  cryptoOnchainHint: string;
  cryptoOffchainHint: string;
  currency: string;
  network: string;
  loading: string;
  redirecting: string;
  getDepositAddress: string;
  continueWithCrypto: string;
  continue: string;
  boltOnHint: string;
  cryptoStartFailed: string;
  paymentStartFailed: string;
  scanToPay: string;
  sendExactly: string;
  copied: string;
  copyAddress: string;
  expiresIn: (countdown: string) => string;
  depositExpired: string;
  overpayment: (received: string, currency: string) => string;
  wrongPayment: (received: string, currency: string) => string;
  underpayment: (received: string, currency: string) => string;
  sendRemainingExact: (remaining: string, currency: string) => string;
  sendRemainingGeneric: string;
  waitingForPayment: string;
  statusSuccessBadge: string;
  statusSuccessTitle: string;
  statusSuccessMessage: string;
  statusErrorBadge: string;
  statusErrorTitle: string;
  statusErrorMessage: string;
  statusPendingBadge: string;
  statusPendingTitle: string;
  statusPendingMessage: string;
  redirectWait: string;
  redirectIn: (seconds: number) => string;
  transactionOverview: string;
  paymentDetails: string;
  secure: string;
  transactionId: string;
  amount: string;
  status: string;
  reason: string;
  copy: string;
  copiedShort: string;
};

const EN: CheckoutCopy = {
  secureCheckout: 'Secure Checkout',
  amountDue: 'Amount due',
  toMerchant: (name) => `to ${name}`,
  paymentMethod: 'Payment method',
  cardMethod: 'Credit / Debit Card',
  cryptoMethod: 'Cryptocurrency',
  alternativePayment: 'Alternative Payment',
  bankMethod: 'Pay by bank',
  chooseBank: 'Choose your bank',
  searchBank: 'Search for your bank',
  noBanksFound: 'No bank matches your search.',
  bankListUnavailable: 'Your bank list couldn\'t be loaded. Continue and choose your bank on the next page.',
  payWithBank: 'Continue to your bank',
  bankRedirectNote: 'You\'ll confirm the transfer securely in your own bank.',
  footerNote: 'Payments are encrypted and securely processed',
  cancel: 'Cancel',
  orderDetails: 'Order details',
  orderIdLabel: 'Order ID',
  amountLabel: 'Amount',
  securePayments: 'Secure payments',
  securityCode: 'Security code',
  creditCard: 'Credit Card',
  cvvHint: '3-digit code on the back of your card. American Express uses a 4-digit code on the front.',
  noOrderDetails: 'No additional order details for this payment.',
  cardNumber: 'Card number',
  cardholderName: 'Cardholder name',
  expiryDate: 'Expiry Date',
  cvv: 'CVV',
  cardNumberPlaceholder: '4242 4283 7159 2646',
  cardholderPlaceholder: 'Full name on card',
  expiryPlaceholder: 'MM/YY',
  cvvPlaceholder: 'CVV',
  otpPlaceholder: '123456',
  payNow: 'Pay now',
  payAmount: (amount, currency) => `Pay ${amount} ${currency}`,
  processing: 'Processing…',
  loadingDetails: 'Loading payment details…',
  linkUnavailable: 'Link unavailable',
  missingCheckoutLink: 'Missing checkout link.',
  invalidCheckoutLink: 'This checkout link is invalid or has expired.',
  cardNumberRequired: 'Card number is required',
  cardNumberInvalid: 'Enter a valid card number',
  nameRequired: 'Name is required',
  required: 'Required',
  expiryFormat: 'MM/YY',
  cvvInvalid: 'Invalid',
  paymentFailed: 'Payment failed. Please check your card details and try again.',
  redirectCard: (label) => `You'll be redirected to complete your ${label} payment securely.`,
  cardLabel: 'card',
  paymentLabel: 'payment',
  verifyPayment: 'Verify your payment',
  otpHint: 'Enter the one-time code sent to your phone to complete this payment.',
  otpLabel: 'One-time code',
  verifying: 'Verifying…',
  verifyAndPay: 'Verify & pay',
  otpFailed: 'OTP verification failed. Please try again with a different card.',
  refresh: 'Refresh',
  refreshing: 'Refreshing…',
  refreshHint: 'Refresh payment status',
  refreshFailed: 'Unable to refresh payment status. Please try again.',
  submitted: 'Submitted',
  cryptoOnchainHint: "You'll get a deposit address to send crypto directly from your own wallet.",
  cryptoOffchainHint:
    "You'll be redirected to complete your payment via crypto. This may also open the KuCoin app if it's installed on your device.",
  currency: 'Currency',
  network: 'Network',
  loading: 'Loading…',
  redirecting: 'Redirecting…',
  getDepositAddress: 'Get Deposit Address',
  continueWithCrypto: 'Continue with Crypto',
  continue: 'Continue',
  boltOnHint: "You'll be redirected to complete your payment via an alternative payment method.",
  cryptoStartFailed: 'Unable to start crypto payment. Please try again.',
  paymentStartFailed: 'Unable to start payment. Please try again.',
  scanToPay: 'Scan to pay',
  sendExactly: 'Send exactly',
  copied: 'Copied!',
  copyAddress: 'Copy address',
  expiresIn: (countdown) => `Expires in ${countdown}`,
  depositExpired: 'This deposit address has expired — refresh to try again.',
  overpayment: (received, currency) =>
    `Overpayment received: ${received} ${currency}. Completing your payment…`,
  wrongPayment: (received, currency) =>
    `Unexpected payment received: ${received} ${currency}. This doesn't match the requested currency/network — please contact support before sending anything else.`,
  underpayment: (received, currency) => `Underpayment received: ${received} ${currency}.`,
  sendRemainingExact: (remaining, currency) =>
    `Please send ${remaining} ${currency} more to the same address to complete this payment.`,
  sendRemainingGeneric: 'Please send the remaining balance to the same address to complete this payment.',
  waitingForPayment: 'Waiting for payment…',
  statusSuccessBadge: 'Payment successful',
  statusSuccessTitle: 'Payment successful',
  statusSuccessMessage: 'Your payment has been completed successfully.',
  statusErrorBadge: 'Payment failed',
  statusErrorTitle: 'Payment failed',
  statusErrorMessage: "We couldn't complete your payment. Please review the transaction details below.",
  statusPendingBadge: 'Payment processing',
  statusPendingTitle: 'Your payment is being processed',
  statusPendingMessage: "We're securely processing your payment. This usually takes a few moments.",
  redirectWait: 'Please wait while we redirect you…',
  redirectIn: (seconds) =>
    `You will be redirected in ${seconds} ${seconds === 1 ? 'second' : 'seconds'}.`,
  transactionOverview: 'Transaction overview',
  paymentDetails: 'Payment details',
  secure: 'Secure',
  transactionId: 'Transaction ID',
  amount: 'Amount',
  status: 'Status',
  reason: 'Reason',
  copy: 'Copy',
  copiedShort: 'Copied',
};

const ES: CheckoutCopy = {
  secureCheckout: 'Pago seguro',
  amountDue: 'Importe a pagar',
  toMerchant: (name) => `para ${name}`,
  paymentMethod: 'Método de pago',
  cardMethod: 'Tarjeta de crédito / débito',
  cryptoMethod: 'Criptomoneda',
  alternativePayment: 'Pago alternativo',
  bankMethod: 'Pagar con tu banco',
  chooseBank: 'Elige tu banco',
  searchBank: 'Busca tu banco',
  noBanksFound: 'Ningún banco coincide con tu búsqueda.',
  bankListUnavailable: 'No se ha podido cargar la lista de bancos. Continúa y elige tu banco en la siguiente página.',
  payWithBank: 'Continuar a tu banco',
  bankRedirectNote: 'Confirmarás la transferencia de forma segura en tu propio banco.',
  footerNote: 'Los pagos se cifran y se procesan de forma segura',
  cancel: 'Cancelar',
  orderDetails: 'Detalles del pedido',
  orderIdLabel: 'ID del pedido',
  amountLabel: 'Importe',
  securePayments: 'Pagos seguros',
  securityCode: 'Código de seguridad',
  creditCard: 'Tarjeta de crédito',
  cvvHint: 'Código de 3 dígitos en el reverso de la tarjeta. American Express usa 4 dígitos en el anverso.',
  noOrderDetails: 'No hay más detalles para este pago.',
  cardNumber: 'Número de tarjeta',
  cardholderName: 'Nombre del titular',
  expiryDate: 'Fecha de caducidad',
  cvv: 'CVV',
  cardNumberPlaceholder: '4242 4283 7159 2646',
  cardholderPlaceholder: 'Nombre completo en la tarjeta',
  expiryPlaceholder: 'MM/AA',
  cvvPlaceholder: 'CVV',
  otpPlaceholder: '123456',
  payNow: 'Pagar ahora',
  payAmount: (amount, currency) => `Pagar ${amount} ${currency}`,
  processing: 'Procesando…',
  loadingDetails: 'Cargando los datos de pago…',
  linkUnavailable: 'Enlace no disponible',
  missingCheckoutLink: 'Falta el enlace de pago.',
  invalidCheckoutLink: 'Este enlace de pago no es válido o ha caducado.',
  cardNumberRequired: 'El número de tarjeta es obligatorio',
  cardNumberInvalid: 'Introduzca un número de tarjeta válido',
  nameRequired: 'El nombre es obligatorio',
  required: 'Obligatorio',
  expiryFormat: 'MM/AA',
  cvvInvalid: 'No válido',
  paymentFailed: 'El pago ha fallado. Compruebe los datos de la tarjeta e inténtelo de nuevo.',
  redirectCard: (label) => `Será redirigido para completar el pago con ${label} de forma segura.`,
  cardLabel: 'tarjeta',
  paymentLabel: 'pago',
  verifyPayment: 'Verifique su pago',
  otpHint: 'Introduzca el código de un solo uso enviado a su teléfono para completar este pago.',
  otpLabel: 'Código de un solo uso',
  verifying: 'Verificando…',
  verifyAndPay: 'Verificar y pagar',
  otpFailed: 'La verificación del código ha fallado. Inténtelo de nuevo con otra tarjeta.',
  refresh: 'Actualizar',
  refreshing: 'Actualizando…',
  refreshHint: 'Actualizar el estado del pago',
  refreshFailed: 'No se ha podido actualizar el estado del pago. Inténtelo de nuevo.',
  submitted: 'Enviado',
  cryptoOnchainHint: 'Recibirá una dirección de depósito para enviar criptomonedas desde su propia cartera.',
  cryptoOffchainHint:
    'Será redirigido para completar el pago con criptomonedas. También puede abrirse la aplicación KuCoin si está instalada en su dispositivo.',
  currency: 'Moneda',
  network: 'Red',
  loading: 'Cargando…',
  redirecting: 'Redirigiendo…',
  getDepositAddress: 'Obtener dirección de depósito',
  continueWithCrypto: 'Continuar con criptomonedas',
  continue: 'Continuar',
  boltOnHint: 'Será redirigido para completar el pago con un método alternativo.',
  cryptoStartFailed: 'No se ha podido iniciar el pago con criptomonedas. Inténtelo de nuevo.',
  paymentStartFailed: 'No se ha podido iniciar el pago. Inténtelo de nuevo.',
  scanToPay: 'Escanear para pagar',
  sendExactly: 'Envíe exactamente',
  copied: '¡Copiado!',
  copyAddress: 'Copiar dirección',
  expiresIn: (countdown) => `Caduca en ${countdown}`,
  depositExpired: 'Esta dirección de depósito ha caducado — actualice para intentarlo de nuevo.',
  overpayment: (received, currency) =>
    `Sobrepago recibido: ${received} ${currency}. Completando su pago…`,
  wrongPayment: (received, currency) =>
    `Pago inesperado recibido: ${received} ${currency}. No coincide con la moneda/red solicitada — contacte con soporte antes de enviar nada más.`,
  underpayment: (received, currency) => `Pago insuficiente recibido: ${received} ${currency}.`,
  sendRemainingExact: (remaining, currency) =>
    `Envíe ${remaining} ${currency} más a la misma dirección para completar este pago.`,
  sendRemainingGeneric: 'Envíe el saldo restante a la misma dirección para completar este pago.',
  waitingForPayment: 'Esperando el pago…',
  statusSuccessBadge: 'Pago realizado',
  statusSuccessTitle: 'Pago realizado',
  statusSuccessMessage: 'Su pago se ha completado correctamente.',
  statusErrorBadge: 'Pago fallido',
  statusErrorTitle: 'Pago fallido',
  statusErrorMessage: 'No hemos podido completar su pago. Revise los detalles de la transacción a continuación.',
  statusPendingBadge: 'Procesando el pago',
  statusPendingTitle: 'Su pago se está procesando',
  statusPendingMessage: 'Estamos procesando su pago de forma segura. Suele tardar unos instantes.',
  redirectWait: 'Espere mientras le redirigimos…',
  redirectIn: (seconds) =>
    `Será redirigido en ${seconds} ${seconds === 1 ? 'segundo' : 'segundos'}.`,
  transactionOverview: 'Resumen de la transacción',
  paymentDetails: 'Detalles del pago',
  secure: 'Seguro',
  transactionId: 'ID de transacción',
  amount: 'Importe',
  status: 'Estado',
  reason: 'Motivo',
  copy: 'Copiar',
  copiedShort: 'Copiado',
};

const FR: CheckoutCopy = {
  secureCheckout: 'Paiement sécurisé',
  amountDue: 'Montant dû',
  toMerchant: (name) => `à ${name}`,
  paymentMethod: 'Moyen de paiement',
  cardMethod: 'Carte de crédit / débit',
  cryptoMethod: 'Cryptomonnaie',
  alternativePayment: 'Paiement alternatif',
  bankMethod: 'Payer avec votre banque',
  chooseBank: 'Choisissez votre banque',
  searchBank: 'Rechercher votre banque',
  noBanksFound: 'Aucune banque ne correspond à votre recherche.',
  bankListUnavailable: 'La liste des banques n\'a pas pu être chargée. Continuez et choisissez votre banque à l\'étape suivante.',
  payWithBank: 'Continuer vers votre banque',
  bankRedirectNote: 'Vous confirmerez le virement en toute sécurité auprès de votre banque.',
  footerNote: 'Les paiements sont chiffrés et traités en toute sécurité',
  cancel: 'Annuler',
  orderDetails: 'Détails de la commande',
  orderIdLabel: 'ID de commande',
  amountLabel: 'Montant',
  securePayments: 'Paiements sécurisés',
  securityCode: 'Code de sécurité',
  creditCard: 'Carte bancaire',
  cvvHint: 'Code à 3 chiffres au dos de la carte. American Express utilise 4 chiffres au recto.',
  noOrderDetails: 'Aucun détail supplémentaire pour ce paiement.',
  cardNumber: 'Numéro de carte',
  cardholderName: 'Nom du titulaire',
  expiryDate: "Date d'expiration",
  cvv: 'CVV',
  cardNumberPlaceholder: '4242 4283 7159 2646',
  cardholderPlaceholder: 'Nom complet sur la carte',
  expiryPlaceholder: 'MM/AA',
  cvvPlaceholder: 'CVV',
  otpPlaceholder: '123456',
  payNow: 'Payer maintenant',
  payAmount: (amount, currency) => `Payer ${amount} ${currency}`,
  processing: 'Traitement…',
  loadingDetails: 'Chargement des informations de paiement…',
  linkUnavailable: 'Lien indisponible',
  missingCheckoutLink: 'Lien de paiement manquant.',
  invalidCheckoutLink: 'Ce lien de paiement est invalide ou a expiré.',
  cardNumberRequired: 'Le numéro de carte est obligatoire',
  cardNumberInvalid: 'Saisissez un numéro de carte valide',
  nameRequired: 'Le nom est obligatoire',
  required: 'Obligatoire',
  expiryFormat: 'MM/AA',
  cvvInvalid: 'Invalide',
  paymentFailed: 'Le paiement a échoué. Vérifiez les informations de votre carte et réessayez.',
  redirectCard: (label) => `Vous allez être redirigé pour finaliser votre paiement ${label} en toute sécurité.`,
  cardLabel: 'carte',
  paymentLabel: 'paiement',
  verifyPayment: 'Vérifiez votre paiement',
  otpHint: 'Saisissez le code à usage unique envoyé sur votre téléphone pour finaliser ce paiement.',
  otpLabel: 'Code à usage unique',
  verifying: 'Vérification…',
  verifyAndPay: 'Vérifier et payer',
  otpFailed: 'La vérification du code a échoué. Réessayez avec une autre carte.',
  refresh: 'Actualiser',
  refreshing: 'Actualisation…',
  refreshHint: 'Actualiser le statut du paiement',
  refreshFailed: "Impossible d'actualiser le statut du paiement. Réessayez.",
  submitted: 'Envoyé',
  cryptoOnchainHint: 'Vous recevrez une adresse de dépôt pour envoyer des crypto-actifs depuis votre propre portefeuille.',
  cryptoOffchainHint:
    "Vous allez être redirigé pour finaliser votre paiement en crypto. L'application KuCoin peut aussi s'ouvrir si elle est installée sur votre appareil.",
  currency: 'Devise',
  network: 'Réseau',
  loading: 'Chargement…',
  redirecting: 'Redirection…',
  getDepositAddress: 'Obtenir l’adresse de dépôt',
  continueWithCrypto: 'Continuer avec la crypto',
  continue: 'Continuer',
  boltOnHint: 'Vous allez être redirigé pour finaliser votre paiement via un moyen alternatif.',
  cryptoStartFailed: 'Impossible de démarrer le paiement crypto. Réessayez.',
  paymentStartFailed: 'Impossible de démarrer le paiement. Réessayez.',
  scanToPay: 'Scanner pour payer',
  sendExactly: 'Envoyez exactement',
  copied: 'Copié !',
  copyAddress: 'Copier l’adresse',
  expiresIn: (countdown) => `Expire dans ${countdown}`,
  depositExpired: 'Cette adresse de dépôt a expiré — actualisez pour réessayer.',
  overpayment: (received, currency) =>
    `Trop-perçu reçu : ${received} ${currency}. Finalisation de votre paiement…`,
  wrongPayment: (received, currency) =>
    `Paiement inattendu reçu : ${received} ${currency}. Cela ne correspond pas à la devise/au réseau demandés — contactez le support avant d’envoyer autre chose.`,
  underpayment: (received, currency) => `Paiement insuffisant reçu : ${received} ${currency}.`,
  sendRemainingExact: (remaining, currency) =>
    `Veuillez envoyer encore ${remaining} ${currency} à la même adresse pour finaliser ce paiement.`,
  sendRemainingGeneric: 'Veuillez envoyer le solde restant à la même adresse pour finaliser ce paiement.',
  waitingForPayment: 'En attente du paiement…',
  statusSuccessBadge: 'Paiement réussi',
  statusSuccessTitle: 'Paiement réussi',
  statusSuccessMessage: 'Votre paiement a été effectué avec succès.',
  statusErrorBadge: 'Paiement échoué',
  statusErrorTitle: 'Paiement échoué',
  statusErrorMessage: 'Nous n’avons pas pu finaliser votre paiement. Consultez les détails de la transaction ci-dessous.',
  statusPendingBadge: 'Paiement en cours',
  statusPendingTitle: 'Votre paiement est en cours de traitement',
  statusPendingMessage: 'Nous traitons votre paiement en toute sécurité. Cela ne prend généralement que quelques instants.',
  redirectWait: 'Veuillez patienter pendant la redirection…',
  redirectIn: (seconds) =>
    `Vous serez redirigé dans ${seconds} ${seconds === 1 ? 'seconde' : 'secondes'}.`,
  transactionOverview: 'Aperçu de la transaction',
  paymentDetails: 'Détails du paiement',
  secure: 'Sécurisé',
  transactionId: 'ID de transaction',
  amount: 'Montant',
  status: 'Statut',
  reason: 'Motif',
  copy: 'Copier',
  copiedShort: 'Copié',
};

const DE: CheckoutCopy = {
  secureCheckout: 'Sichere Zahlung',
  amountDue: 'Fälliger Betrag',
  toMerchant: (name) => `an ${name}`,
  paymentMethod: 'Zahlungsmethode',
  cardMethod: 'Kredit- / Debitkarte',
  cryptoMethod: 'Kryptowährung',
  alternativePayment: 'Alternative Zahlung',
  bankMethod: 'Mit Ihrer Bank bezahlen',
  chooseBank: 'Wählen Sie Ihre Bank',
  searchBank: 'Bank suchen',
  noBanksFound: 'Keine Bank entspricht Ihrer Suche.',
  bankListUnavailable: 'Die Bankenliste konnte nicht geladen werden. Fahren Sie fort und wählen Sie Ihre Bank auf der nächsten Seite.',
  payWithBank: 'Weiter zu Ihrer Bank',
  bankRedirectNote: 'Sie bestätigen die Überweisung sicher bei Ihrer eigenen Bank.',
  footerNote: 'Zahlungen werden verschlüsselt und sicher verarbeitet',
  cancel: 'Abbrechen',
  orderDetails: 'Bestelldetails',
  orderIdLabel: 'Bestell-ID',
  amountLabel: 'Betrag',
  securePayments: 'Sichere Zahlungen',
  securityCode: 'Sicherheitscode',
  creditCard: 'Kreditkarte',
  cvvHint: '3-stelliger Code auf der Rückseite der Karte. American Express verwendet 4 Ziffern auf der Vorderseite.',
  noOrderDetails: 'Keine weiteren Details zu dieser Zahlung.',
  cardNumber: 'Kartennummer',
  cardholderName: 'Name des Karteninhabers',
  expiryDate: 'Ablaufdatum',
  cvv: 'CVV',
  cardNumberPlaceholder: '4242 4283 7159 2646',
  cardholderPlaceholder: 'Vollständiger Name auf der Karte',
  expiryPlaceholder: 'MM/JJ',
  cvvPlaceholder: 'CVV',
  otpPlaceholder: '123456',
  payNow: 'Jetzt bezahlen',
  payAmount: (amount, currency) => `${amount} ${currency} bezahlen`,
  processing: 'Wird verarbeitet…',
  loadingDetails: 'Zahlungsdaten werden geladen…',
  linkUnavailable: 'Link nicht verfügbar',
  missingCheckoutLink: 'Zahlungslink fehlt.',
  invalidCheckoutLink: 'Dieser Zahlungslink ist ungültig oder abgelaufen.',
  cardNumberRequired: 'Kartennummer ist erforderlich',
  cardNumberInvalid: 'Geben Sie eine gültige Kartennummer ein',
  nameRequired: 'Name ist erforderlich',
  required: 'Erforderlich',
  expiryFormat: 'MM/JJ',
  cvvInvalid: 'Ungültig',
  paymentFailed: 'Zahlung fehlgeschlagen. Prüfen Sie Ihre Kartendaten und versuchen Sie es erneut.',
  redirectCard: (label) => `Sie werden weitergeleitet, um Ihre ${label}-Zahlung sicher abzuschließen.`,
  cardLabel: 'Karten',
  paymentLabel: 'Zahlungs',
  verifyPayment: 'Zahlung bestätigen',
  otpHint: 'Geben Sie den Einmalcode ein, der an Ihr Telefon gesendet wurde, um diese Zahlung abzuschließen.',
  otpLabel: 'Einmalcode',
  verifying: 'Wird geprüft…',
  verifyAndPay: 'Bestätigen und bezahlen',
  otpFailed: 'Codeprüfung fehlgeschlagen. Versuchen Sie es mit einer anderen Karte erneut.',
  refresh: 'Aktualisieren',
  refreshing: 'Wird aktualisiert…',
  refreshHint: 'Zahlungsstatus aktualisieren',
  refreshFailed: 'Zahlungsstatus konnte nicht aktualisiert werden. Bitte erneut versuchen.',
  submitted: 'Gesendet',
  cryptoOnchainHint: 'Sie erhalten eine Einzahlungsadresse, um Krypto direkt von Ihrer eigenen Wallet zu senden.',
  cryptoOffchainHint:
    'Sie werden weitergeleitet, um Ihre Zahlung per Krypto abzuschließen. Die KuCoin-App kann sich öffnen, wenn sie auf Ihrem Gerät installiert ist.',
  currency: 'Währung',
  network: 'Netzwerk',
  loading: 'Wird geladen…',
  redirecting: 'Weiterleitung…',
  getDepositAddress: 'Einzahlungsadresse abrufen',
  continueWithCrypto: 'Mit Krypto fortfahren',
  continue: 'Weiter',
  boltOnHint: 'Sie werden weitergeleitet, um Ihre Zahlung über eine alternative Methode abzuschließen.',
  cryptoStartFailed: 'Krypto-Zahlung konnte nicht gestartet werden. Bitte erneut versuchen.',
  paymentStartFailed: 'Zahlung konnte nicht gestartet werden. Bitte erneut versuchen.',
  scanToPay: 'Zum Bezahlen scannen',
  sendExactly: 'Genau senden',
  copied: 'Kopiert!',
  copyAddress: 'Adresse kopieren',
  expiresIn: (countdown) => `Läuft ab in ${countdown}`,
  depositExpired: 'Diese Einzahlungsadresse ist abgelaufen — aktualisieren Sie die Seite, um es erneut zu versuchen.',
  overpayment: (received, currency) =>
    `Überzahlung erhalten: ${received} ${currency}. Zahlung wird abgeschlossen…`,
  wrongPayment: (received, currency) =>
    `Unerwartete Zahlung erhalten: ${received} ${currency}. Das entspricht nicht der angeforderten Währung/dem Netzwerk — kontaktieren Sie den Support, bevor Sie etwas weiter senden.`,
  underpayment: (received, currency) => `Unterzahlung erhalten: ${received} ${currency}.`,
  sendRemainingExact: (remaining, currency) =>
    `Bitte senden Sie weitere ${remaining} ${currency} an dieselbe Adresse, um diese Zahlung abzuschließen.`,
  sendRemainingGeneric: 'Bitte senden Sie den Restbetrag an dieselbe Adresse, um diese Zahlung abzuschließen.',
  waitingForPayment: 'Warten auf Zahlung…',
  statusSuccessBadge: 'Zahlung erfolgreich',
  statusSuccessTitle: 'Zahlung erfolgreich',
  statusSuccessMessage: 'Ihre Zahlung wurde erfolgreich abgeschlossen.',
  statusErrorBadge: 'Zahlung fehlgeschlagen',
  statusErrorTitle: 'Zahlung fehlgeschlagen',
  statusErrorMessage: 'Wir konnten Ihre Zahlung nicht abschließen. Bitte prüfen Sie die Transaktionsdetails unten.',
  statusPendingBadge: 'Zahlung wird verarbeitet',
  statusPendingTitle: 'Ihre Zahlung wird verarbeitet',
  statusPendingMessage: 'Wir verarbeiten Ihre Zahlung sicher. Das dauert in der Regel nur wenige Augenblicke.',
  redirectWait: 'Bitte warten Sie, während wir Sie weiterleiten…',
  redirectIn: (seconds) =>
    `Sie werden in ${seconds} ${seconds === 1 ? 'Sekunde' : 'Sekunden'} weitergeleitet.`,
  transactionOverview: 'Transaktionsübersicht',
  paymentDetails: 'Zahlungsdetails',
  secure: 'Sicher',
  transactionId: 'Transaktions-ID',
  amount: 'Betrag',
  status: 'Status',
  reason: 'Grund',
  copy: 'Kopieren',
  copiedShort: 'Kopiert',
};

const IT: CheckoutCopy = {
  secureCheckout: 'Pagamento sicuro',
  amountDue: 'Importo dovuto',
  toMerchant: (name) => `a ${name}`,
  paymentMethod: 'Metodo di pagamento',
  cardMethod: 'Carta di credito / debito',
  cryptoMethod: 'Criptovaluta',
  alternativePayment: 'Pagamento alternativo',
  bankMethod: 'Paga con la tua banca',
  chooseBank: 'Scegli la tua banca',
  searchBank: 'Cerca la tua banca',
  noBanksFound: 'Nessuna banca corrisponde alla ricerca.',
  bankListUnavailable: 'Impossibile caricare l\'elenco delle banche. Continua e scegli la tua banca nella pagina successiva.',
  payWithBank: 'Continua verso la tua banca',
  bankRedirectNote: 'Confermerai il bonifico in modo sicuro presso la tua banca.',
  footerNote: 'I pagamenti sono crittografati e elaborati in modo sicuro',
  cancel: 'Annulla',
  orderDetails: 'Dettagli ordine',
  orderIdLabel: 'ID ordine',
  amountLabel: 'Importo',
  securePayments: 'Pagamenti sicuri',
  securityCode: 'Codice di sicurezza',
  creditCard: 'Carta di credito',
  cvvHint: 'Codice di 3 cifre sul retro della carta. American Express usa 4 cifre sul fronte.',
  noOrderDetails: 'Nessun dettaglio aggiuntivo per questo pagamento.',
  cardNumber: 'Numero della carta',
  cardholderName: 'Nome del titolare',
  expiryDate: 'Data di scadenza',
  cvv: 'CVV',
  cardNumberPlaceholder: '4242 4283 7159 2646',
  cardholderPlaceholder: 'Nome completo sulla carta',
  expiryPlaceholder: 'MM/AA',
  cvvPlaceholder: 'CVV',
  otpPlaceholder: '123456',
  payNow: 'Paga ora',
  payAmount: (amount, currency) => `Paga ${amount} ${currency}`,
  processing: 'Elaborazione…',
  loadingDetails: 'Caricamento dei dati di pagamento…',
  linkUnavailable: 'Link non disponibile',
  missingCheckoutLink: 'Link di pagamento mancante.',
  invalidCheckoutLink: 'Questo link di pagamento non è valido o è scaduto.',
  cardNumberRequired: 'Il numero della carta è obbligatorio',
  cardNumberInvalid: 'Inserisci un numero di carta valido',
  nameRequired: 'Il nome è obbligatorio',
  required: 'Obbligatorio',
  expiryFormat: 'MM/AA',
  cvvInvalid: 'Non valido',
  paymentFailed: 'Pagamento non riuscito. Controlla i dati della carta e riprova.',
  redirectCard: (label) => `Verrai reindirizzato per completare in modo sicuro il pagamento con ${label}.`,
  cardLabel: 'carta',
  paymentLabel: 'pagamento',
  verifyPayment: 'Verifica il pagamento',
  otpHint: 'Inserisci il codice usa e getta inviato al telefono per completare questo pagamento.',
  otpLabel: 'Codice usa e getta',
  verifying: 'Verifica…',
  verifyAndPay: 'Verifica e paga',
  otpFailed: 'Verifica del codice non riuscita. Riprova con un’altra carta.',
  refresh: 'Aggiorna',
  refreshing: 'Aggiornamento…',
  refreshHint: 'Aggiorna lo stato del pagamento',
  refreshFailed: 'Impossibile aggiornare lo stato del pagamento. Riprova.',
  submitted: 'Inviato',
  cryptoOnchainHint: 'Riceverai un indirizzo di deposito per inviare cripto direttamente dal tuo wallet.',
  cryptoOffchainHint:
    'Verrai reindirizzato per completare il pagamento in cripto. Potrebbe anche aprirsi l’app KuCoin se è installata sul dispositivo.',
  currency: 'Valuta',
  network: 'Rete',
  loading: 'Caricamento…',
  redirecting: 'Reindirizzamento…',
  getDepositAddress: 'Ottieni indirizzo di deposito',
  continueWithCrypto: 'Continua con cripto',
  continue: 'Continua',
  boltOnHint: 'Verrai reindirizzato per completare il pagamento con un metodo alternativo.',
  cryptoStartFailed: 'Impossibile avviare il pagamento in cripto. Riprova.',
  paymentStartFailed: 'Impossibile avviare il pagamento. Riprova.',
  scanToPay: 'Scansiona per pagare',
  sendExactly: 'Invia esattamente',
  copied: 'Copiato!',
  copyAddress: 'Copia indirizzo',
  expiresIn: (countdown) => `Scade tra ${countdown}`,
  depositExpired: 'Questo indirizzo di deposito è scaduto — aggiorna per riprovare.',
  overpayment: (received, currency) =>
    `Pagamento in eccesso ricevuto: ${received} ${currency}. Completamento del pagamento…`,
  wrongPayment: (received, currency) =>
    `Pagamento inatteso ricevuto: ${received} ${currency}. Non corrisponde alla valuta/rete richiesta — contatta l’assistenza prima di inviare altro.`,
  underpayment: (received, currency) => `Pagamento insufficiente ricevuto: ${received} ${currency}.`,
  sendRemainingExact: (remaining, currency) =>
    `Invia altri ${remaining} ${currency} allo stesso indirizzo per completare questo pagamento.`,
  sendRemainingGeneric: 'Invia il saldo rimanente allo stesso indirizzo per completare questo pagamento.',
  waitingForPayment: 'In attesa del pagamento…',
  statusSuccessBadge: 'Pagamento riuscito',
  statusSuccessTitle: 'Pagamento riuscito',
  statusSuccessMessage: 'Il pagamento è stato completato correttamente.',
  statusErrorBadge: 'Pagamento non riuscito',
  statusErrorTitle: 'Pagamento non riuscito',
  statusErrorMessage: 'Non è stato possibile completare il pagamento. Controlla i dettagli della transazione qui sotto.',
  statusPendingBadge: 'Pagamento in elaborazione',
  statusPendingTitle: 'Il pagamento è in elaborazione',
  statusPendingMessage: 'Stiamo elaborando il pagamento in modo sicuro. Di solito richiede pochi istanti.',
  redirectWait: 'Attendi mentre ti reindirizziamo…',
  redirectIn: (seconds) =>
    `Verrai reindirizzato tra ${seconds} ${seconds === 1 ? 'secondo' : 'secondi'}.`,
  transactionOverview: 'Riepilogo transazione',
  paymentDetails: 'Dettagli del pagamento',
  secure: 'Sicuro',
  transactionId: 'ID transazione',
  amount: 'Importo',
  status: 'Stato',
  reason: 'Motivo',
  copy: 'Copia',
  copiedShort: 'Copiato',
};

const PT: CheckoutCopy = {
  secureCheckout: 'Pagamento seguro',
  amountDue: 'Valor a pagar',
  toMerchant: (name) => `para ${name}`,
  paymentMethod: 'Método de pagamento',
  cardMethod: 'Cartão de crédito / débito',
  cryptoMethod: 'Criptomoeda',
  alternativePayment: 'Pagamento alternativo',
  bankMethod: 'Pagar com o seu banco',
  chooseBank: 'Escolha o seu banco',
  searchBank: 'Procure o seu banco',
  noBanksFound: 'Nenhum banco corresponde à sua pesquisa.',
  bankListUnavailable: 'Não foi possível carregar a lista de bancos. Continue e escolha o seu banco na página seguinte.',
  payWithBank: 'Continuar para o seu banco',
  bankRedirectNote: 'Irá confirmar a transferência em segurança no seu próprio banco.',
  footerNote: 'Os pagamentos são criptografados e processados com segurança',
  cancel: 'Cancelar',
  orderDetails: 'Detalhes do pedido',
  orderIdLabel: 'ID do pedido',
  amountLabel: 'Valor',
  securePayments: 'Pagamentos seguros',
  securityCode: 'Código de segurança',
  creditCard: 'Cartão de crédito',
  cvvHint: 'Código de 3 dígitos no verso do cartão. A American Express usa 4 dígitos na frente.',
  noOrderDetails: 'Não há detalhes adicionais para este pagamento.',
  cardNumber: 'Número do cartão',
  cardholderName: 'Nome do titular',
  expiryDate: 'Data de validade',
  cvv: 'CVV',
  cardNumberPlaceholder: '4242 4283 7159 2646',
  cardholderPlaceholder: 'Nome completo no cartão',
  expiryPlaceholder: 'MM/AA',
  cvvPlaceholder: 'CVV',
  otpPlaceholder: '123456',
  payNow: 'Pagar agora',
  payAmount: (amount, currency) => `Pagar ${amount} ${currency}`,
  processing: 'Processando…',
  loadingDetails: 'Carregando os dados de pagamento…',
  linkUnavailable: 'Link indisponível',
  missingCheckoutLink: 'Falta o link de pagamento.',
  invalidCheckoutLink: 'Este link de pagamento é inválido ou expirou.',
  cardNumberRequired: 'O número do cartão é obrigatório',
  cardNumberInvalid: 'Digite um número de cartão válido',
  nameRequired: 'O nome é obrigatório',
  required: 'Obrigatório',
  expiryFormat: 'MM/AA',
  cvvInvalid: 'Inválido',
  paymentFailed: 'O pagamento falhou. Verifique os dados do cartão e tente novamente.',
  redirectCard: (label) => `Você será redirecionado para concluir o pagamento com ${label} com segurança.`,
  cardLabel: 'cartão',
  paymentLabel: 'pagamento',
  verifyPayment: 'Verifique seu pagamento',
  otpHint: 'Digite o código de uso único enviado ao seu telefone para concluir este pagamento.',
  otpLabel: 'Código de uso único',
  verifying: 'Verificando…',
  verifyAndPay: 'Verificar e pagar',
  otpFailed: 'A verificação do código falhou. Tente novamente com outro cartão.',
  refresh: 'Atualizar',
  refreshing: 'Atualizando…',
  refreshHint: 'Atualizar o status do pagamento',
  refreshFailed: 'Não foi possível atualizar o status do pagamento. Tente novamente.',
  submitted: 'Enviado',
  cryptoOnchainHint: 'Você receberá um endereço de depósito para enviar cripto diretamente da sua carteira.',
  cryptoOffchainHint:
    'Você será redirecionado para concluir o pagamento via cripto. O app KuCoin também pode abrir se estiver instalado no dispositivo.',
  currency: 'Moeda',
  network: 'Rede',
  loading: 'Carregando…',
  redirecting: 'Redirecionando…',
  getDepositAddress: 'Obter endereço de depósito',
  continueWithCrypto: 'Continuar com cripto',
  continue: 'Continuar',
  boltOnHint: 'Você será redirecionado para concluir o pagamento por um método alternativo.',
  cryptoStartFailed: 'Não foi possível iniciar o pagamento em cripto. Tente novamente.',
  paymentStartFailed: 'Não foi possível iniciar o pagamento. Tente novamente.',
  scanToPay: 'Escaneie para pagar',
  sendExactly: 'Envie exatamente',
  copied: 'Copiado!',
  copyAddress: 'Copiar endereço',
  expiresIn: (countdown) => `Expira em ${countdown}`,
  depositExpired: 'Este endereço de depósito expirou — atualize para tentar novamente.',
  overpayment: (received, currency) =>
    `Pagamento a maior recebido: ${received} ${currency}. Concluindo seu pagamento…`,
  wrongPayment: (received, currency) =>
    `Pagamento inesperado recebido: ${received} ${currency}. Isso não corresponde à moeda/rede solicitada — fale com o suporte antes de enviar mais.`,
  underpayment: (received, currency) => `Pagamento insuficiente recebido: ${received} ${currency}.`,
  sendRemainingExact: (remaining, currency) =>
    `Envie mais ${remaining} ${currency} para o mesmo endereço para concluir este pagamento.`,
  sendRemainingGeneric: 'Envie o saldo restante para o mesmo endereço para concluir este pagamento.',
  waitingForPayment: 'Aguardando o pagamento…',
  statusSuccessBadge: 'Pagamento concluído',
  statusSuccessTitle: 'Pagamento concluído',
  statusSuccessMessage: 'Seu pagamento foi concluído com sucesso.',
  statusErrorBadge: 'Pagamento falhou',
  statusErrorTitle: 'Pagamento falhou',
  statusErrorMessage: 'Não foi possível concluir seu pagamento. Revise os detalhes da transação abaixo.',
  statusPendingBadge: 'Processando pagamento',
  statusPendingTitle: 'Seu pagamento está sendo processado',
  statusPendingMessage: 'Estamos processando seu pagamento com segurança. Isso costuma levar alguns instantes.',
  redirectWait: 'Aguarde enquanto redirecionamos você…',
  redirectIn: (seconds) =>
    `Você será redirecionado em ${seconds} ${seconds === 1 ? 'segundo' : 'segundos'}.`,
  transactionOverview: 'Visão geral da transação',
  paymentDetails: 'Detalhes do pagamento',
  secure: 'Seguro',
  transactionId: 'ID da transação',
  amount: 'Valor',
  status: 'Status',
  reason: 'Motivo',
  copy: 'Copiar',
  copiedShort: 'Copiado',
};

const NL: CheckoutCopy = {
  secureCheckout: 'Veilige betaling',
  amountDue: 'Te betalen bedrag',
  toMerchant: (name) => `aan ${name}`,
  paymentMethod: 'Betaalmethode',
  cardMethod: 'Credit- / debitcard',
  cryptoMethod: 'Cryptovaluta',
  alternativePayment: 'Alternatieve betaling',
  bankMethod: 'Betalen via je bank',
  chooseBank: 'Kies je bank',
  searchBank: 'Zoek je bank',
  noBanksFound: 'Geen bank gevonden voor je zoekopdracht.',
  bankListUnavailable: 'De lijst met banken kon niet worden geladen. Ga verder en kies je bank op de volgende pagina.',
  payWithBank: 'Verder naar je bank',
  bankRedirectNote: 'Je bevestigt de overboeking veilig in je eigen bank.',
  footerNote: 'Betalingen worden versleuteld en veilig verwerkt',
  cancel: 'Annuleren',
  orderDetails: 'Bestelgegevens',
  orderIdLabel: 'Bestel-ID',
  amountLabel: 'Bedrag',
  securePayments: 'Veilige betalingen',
  securityCode: 'Beveiligingscode',
  creditCard: 'Creditcard',
  cvvHint: '3-cijferige code op de achterkant van de kaart. American Express gebruikt 4 cijfers op de voorkant.',
  noOrderDetails: 'Geen extra details voor deze betaling.',
  cardNumber: 'Kaartnummer',
  cardholderName: 'Naam kaarthouder',
  expiryDate: 'Vervaldatum',
  cvv: 'CVV',
  cardNumberPlaceholder: '4242 4283 7159 2646',
  cardholderPlaceholder: 'Volledige naam op de kaart',
  expiryPlaceholder: 'MM/JJ',
  cvvPlaceholder: 'CVV',
  otpPlaceholder: '123456',
  payNow: 'Nu betalen',
  payAmount: (amount, currency) => `Betaal ${amount} ${currency}`,
  processing: 'Verwerken…',
  loadingDetails: 'Betalingsgegevens laden…',
  linkUnavailable: 'Link niet beschikbaar',
  missingCheckoutLink: 'Betaallink ontbreekt.',
  invalidCheckoutLink: 'Deze betaallink is ongeldig of verlopen.',
  cardNumberRequired: 'Kaartnummer is verplicht',
  cardNumberInvalid: 'Voer een geldig kaartnummer in',
  nameRequired: 'Naam is verplicht',
  required: 'Verplicht',
  expiryFormat: 'MM/JJ',
  cvvInvalid: 'Ongeldig',
  paymentFailed: 'Betaling mislukt. Controleer uw kaartgegevens en probeer het opnieuw.',
  redirectCard: (label) => `U wordt doorgestuurd om uw ${label}betaling veilig af te ronden.`,
  cardLabel: 'kaart',
  paymentLabel: 'betalings',
  verifyPayment: 'Bevestig uw betaling',
  otpHint: 'Voer de eenmalige code in die naar uw telefoon is gestuurd om deze betaling af te ronden.',
  otpLabel: 'Eenmalige code',
  verifying: 'Controleren…',
  verifyAndPay: 'Bevestigen en betalen',
  otpFailed: 'Codeverificatie mislukt. Probeer het opnieuw met een andere kaart.',
  refresh: 'Vernieuwen',
  refreshing: 'Vernieuwen…',
  refreshHint: 'Betalingsstatus vernieuwen',
  refreshFailed: 'Betalingsstatus kon niet worden vernieuwd. Probeer het opnieuw.',
  submitted: 'Verzonden',
  cryptoOnchainHint: 'U ontvangt een stortingsadres om crypto rechtstreeks vanaf uw eigen wallet te verzenden.',
  cryptoOffchainHint:
    'U wordt doorgestuurd om uw betaling via crypto af te ronden. De KuCoin-app kan ook openen als deze op uw apparaat is geïnstalleerd.',
  currency: 'Valuta',
  network: 'Netwerk',
  loading: 'Laden…',
  redirecting: 'Doorsturen…',
  getDepositAddress: 'Stortingsadres ophalen',
  continueWithCrypto: 'Doorgaan met crypto',
  continue: 'Doorgaan',
  boltOnHint: 'U wordt doorgestuurd om uw betaling via een alternatieve methode af te ronden.',
  cryptoStartFailed: 'Cryptobetaling kon niet worden gestart. Probeer het opnieuw.',
  paymentStartFailed: 'Betaling kon niet worden gestart. Probeer het opnieuw.',
  scanToPay: 'Scan om te betalen',
  sendExactly: 'Verstuur precies',
  copied: 'Gekopieerd!',
  copyAddress: 'Adres kopiëren',
  expiresIn: (countdown) => `Verloopt over ${countdown}`,
  depositExpired: 'Dit stortingsadres is verlopen — vernieuw om het opnieuw te proberen.',
  overpayment: (received, currency) =>
    `Te veel ontvangen: ${received} ${currency}. Betaling wordt afgerond…`,
  wrongPayment: (received, currency) =>
    `Onverwachte betaling ontvangen: ${received} ${currency}. Dit komt niet overeen met de gevraagde valuta/het netwerk — neem contact op met support voordat u meer verstuurt.`,
  underpayment: (received, currency) => `Te weinig ontvangen: ${received} ${currency}.`,
  sendRemainingExact: (remaining, currency) =>
    `Stuur nog ${remaining} ${currency} naar hetzelfde adres om deze betaling af te ronden.`,
  sendRemainingGeneric: 'Stuur het resterende bedrag naar hetzelfde adres om deze betaling af te ronden.',
  waitingForPayment: 'Wachten op betaling…',
  statusSuccessBadge: 'Betaling geslaagd',
  statusSuccessTitle: 'Betaling geslaagd',
  statusSuccessMessage: 'Uw betaling is succesvol afgerond.',
  statusErrorBadge: 'Betaling mislukt',
  statusErrorTitle: 'Betaling mislukt',
  statusErrorMessage: 'We konden uw betaling niet afronden. Bekijk de transactiegegevens hieronder.',
  statusPendingBadge: 'Betaling wordt verwerkt',
  statusPendingTitle: 'Uw betaling wordt verwerkt',
  statusPendingMessage: 'We verwerken uw betaling veilig. Dit duurt meestal enkele ogenblikken.',
  redirectWait: 'Even geduld terwijl we u doorsturen…',
  redirectIn: (seconds) =>
    `U wordt over ${seconds} ${seconds === 1 ? 'seconde' : 'seconden'} doorgestuurd.`,
  transactionOverview: 'Transactieoverzicht',
  paymentDetails: 'Betalingsgegevens',
  secure: 'Veilig',
  transactionId: 'Transactie-ID',
  amount: 'Bedrag',
  status: 'Status',
  reason: 'Reden',
  copy: 'Kopiëren',
  copiedShort: 'Gekopieerd',
};

const PL: CheckoutCopy = {
  secureCheckout: 'Bezpieczna płatność',
  amountDue: 'Kwota do zapłaty',
  toMerchant: (name) => `dla ${name}`,
  paymentMethod: 'Metoda płatności',
  cardMethod: 'Karta kredytowa / debetowa',
  cryptoMethod: 'Kryptowaluta',
  alternativePayment: 'Płatność alternatywna',
  bankMethod: 'Zapłać przez bank',
  chooseBank: 'Wybierz swój bank',
  searchBank: 'Wyszukaj swój bank',
  noBanksFound: 'Żaden bank nie pasuje do wyszukiwania.',
  bankListUnavailable: 'Nie udało się załadować listy banków. Przejdź dalej i wybierz bank na następnej stronie.',
  payWithBank: 'Przejdź do swojego banku',
  bankRedirectNote: 'Przelew potwierdzisz bezpiecznie we własnym banku.',
  footerNote: 'Płatności są szyfrowane i bezpiecznie przetwarzane',
  cancel: 'Anuluj',
  orderDetails: 'Szczegóły zamówienia',
  orderIdLabel: 'ID zamówienia',
  amountLabel: 'Kwota',
  securePayments: 'Bezpieczne płatności',
  securityCode: 'Kod zabezpieczający',
  creditCard: 'Karta kredytowa',
  cvvHint: '3-cyfrowy kod na odwrocie karty. American Express używa 4 cyfr na przodzie.',
  noOrderDetails: 'Brak dodatkowych szczegółów tej płatności.',
  cardNumber: 'Numer karty',
  cardholderName: 'Imię i nazwisko posiadacza',
  expiryDate: 'Data ważności',
  cvv: 'CVV',
  cardNumberPlaceholder: '4242 4283 7159 2646',
  cardholderPlaceholder: 'Pełne imię i nazwisko na karcie',
  expiryPlaceholder: 'MM/RR',
  cvvPlaceholder: 'CVV',
  otpPlaceholder: '123456',
  payNow: 'Zapłać teraz',
  payAmount: (amount, currency) => `Zapłać ${amount} ${currency}`,
  processing: 'Przetwarzanie…',
  loadingDetails: 'Ładowanie danych płatności…',
  linkUnavailable: 'Link niedostępny',
  missingCheckoutLink: 'Brak linku do płatności.',
  invalidCheckoutLink: 'Ten link do płatności jest nieprawidłowy lub wygasł.',
  cardNumberRequired: 'Numer karty jest wymagany',
  cardNumberInvalid: 'Wprowadź prawidłowy numer karty',
  nameRequired: 'Imię i nazwisko jest wymagane',
  required: 'Wymagane',
  expiryFormat: 'MM/RR',
  cvvInvalid: 'Nieprawidłowe',
  paymentFailed: 'Płatność nie powiodła się. Sprawdź dane karty i spróbuj ponownie.',
  redirectCard: (label) => `Zostaniesz przekierowany, aby bezpiecznie dokończyć płatność ${label}.`,
  cardLabel: 'kartą',
  paymentLabel: 'płatności',
  verifyPayment: 'Potwierdź płatność',
  otpHint: 'Wprowadź jednorazowy kod wysłany na telefon, aby dokończyć tę płatność.',
  otpLabel: 'Kod jednorazowy',
  verifying: 'Weryfikacja…',
  verifyAndPay: 'Potwierdź i zapłać',
  otpFailed: 'Weryfikacja kodu nie powiodła się. Spróbuj ponownie inną kartą.',
  refresh: 'Odśwież',
  refreshing: 'Odświeżanie…',
  refreshHint: 'Odśwież status płatności',
  refreshFailed: 'Nie udało się odświeżyć statusu płatności. Spróbuj ponownie.',
  submitted: 'Wysłano',
  cryptoOnchainHint: 'Otrzymasz adres depozytu, aby wysłać krypto bezpośrednio z własnego portfela.',
  cryptoOffchainHint:
    'Zostaniesz przekierowany, aby dokończyć płatność krypto. Może też otworzyć się aplikacja KuCoin, jeśli jest zainstalowana na urządzeniu.',
  currency: 'Waluta',
  network: 'Sieć',
  loading: 'Ładowanie…',
  redirecting: 'Przekierowanie…',
  getDepositAddress: 'Pobierz adres depozytu',
  continueWithCrypto: 'Kontynuuj z krypto',
  continue: 'Kontynuuj',
  boltOnHint: 'Zostaniesz przekierowany, aby dokończyć płatność inną metodą.',
  cryptoStartFailed: 'Nie udało się rozpocząć płatności krypto. Spróbuj ponownie.',
  paymentStartFailed: 'Nie udało się rozpocząć płatności. Spróbuj ponownie.',
  scanToPay: 'Zeskanuj, aby zapłacić',
  sendExactly: 'Wyślij dokładnie',
  copied: 'Skopiowano!',
  copyAddress: 'Kopiuj adres',
  expiresIn: (countdown) => `Wygasa za ${countdown}`,
  depositExpired: 'Ten adres depozytu wygasł — odśwież, aby spróbować ponownie.',
  overpayment: (received, currency) =>
    `Otrzymano nadpłatę: ${received} ${currency}. Kończenie płatności…`,
  wrongPayment: (received, currency) =>
    `Otrzymano nieoczekiwaną płatność: ${received} ${currency}. To nie zgadza się z żądaną walutą/siecią — skontaktuj się z pomocą, zanim wyślesz cokolwiek więcej.`,
  underpayment: (received, currency) => `Otrzymano niedopłatę: ${received} ${currency}.`,
  sendRemainingExact: (remaining, currency) =>
    `Wyślij jeszcze ${remaining} ${currency} na ten sam adres, aby dokończyć tę płatność.`,
  sendRemainingGeneric: 'Wyślij pozostałą kwotę na ten sam adres, aby dokończyć tę płatność.',
  waitingForPayment: 'Oczekiwanie na płatność…',
  statusSuccessBadge: 'Płatność zakończona',
  statusSuccessTitle: 'Płatność zakończona',
  statusSuccessMessage: 'Twoja płatność została pomyślnie zakończona.',
  statusErrorBadge: 'Płatność nie powiodła się',
  statusErrorTitle: 'Płatność nie powiodła się',
  statusErrorMessage: 'Nie udało się dokończyć płatności. Sprawdź szczegóły transakcji poniżej.',
  statusPendingBadge: 'Przetwarzanie płatności',
  statusPendingTitle: 'Twoja płatność jest przetwarzana',
  statusPendingMessage: 'Bezpiecznie przetwarzamy Twoją płatność. Zwykle trwa to kilka chwil.',
  redirectWait: 'Poczekaj, trwa przekierowanie…',
  redirectIn: (seconds) =>
    `Zostaniesz przekierowany za ${seconds} ${seconds === 1 ? 'sekundę' : 'sek.'}.`,
  transactionOverview: 'Przegląd transakcji',
  paymentDetails: 'Szczegóły płatności',
  secure: 'Bezpieczne',
  transactionId: 'ID transakcji',
  amount: 'Kwota',
  status: 'Status',
  reason: 'Powód',
  copy: 'Kopiuj',
  copiedShort: 'Skopiowano',
};

const TR: CheckoutCopy = {
  secureCheckout: 'Güvenli ödeme',
  amountDue: 'Ödenecek tutar',
  toMerchant: (name) => `${name} için`,
  paymentMethod: 'Ödeme yöntemi',
  cardMethod: 'Kredi / banka kartı',
  cryptoMethod: 'Kripto para',
  alternativePayment: 'Alternatif ödeme',
  bankMethod: 'Bankanızla ödeyin',
  chooseBank: 'Bankanızı seçin',
  searchBank: 'Bankanızı arayın',
  noBanksFound: 'Aramanızla eşleşen banka yok.',
  bankListUnavailable: 'Banka listesi yüklenemedi. Devam edin ve bankanızı sonraki sayfada seçin.',
  payWithBank: 'Bankanıza devam edin',
  bankRedirectNote: 'Havaleyi kendi bankanızda güvenle onaylayacaksınız.',
  footerNote: 'Ödemeler şifrelenir ve güvenli şekilde işlenir',
  cancel: 'İptal',
  orderDetails: 'Sipariş ayrıntıları',
  orderIdLabel: 'Sipariş kimliği',
  amountLabel: 'Tutar',
  securePayments: 'Güvenli ödemeler',
  securityCode: 'Güvenlik kodu',
  creditCard: 'Kredi kartı',
  cvvHint: 'Kartın arkasındaki 3 haneli kod. American Express ön yüzde 4 haneli kod kullanır.',
  noOrderDetails: 'Bu ödeme için ek ayrıntı yok.',
  cardNumber: 'Kart numarası',
  cardholderName: 'Kart sahibinin adı',
  expiryDate: 'Son kullanma tarihi',
  cvv: 'CVV',
  cardNumberPlaceholder: '4242 4283 7159 2646',
  cardholderPlaceholder: 'Karttaki tam ad',
  expiryPlaceholder: 'AA/YY',
  cvvPlaceholder: 'CVV',
  otpPlaceholder: '123456',
  payNow: 'Şimdi öde',
  payAmount: (amount, currency) => `${amount} ${currency} öde`,
  processing: 'İşleniyor…',
  loadingDetails: 'Ödeme bilgileri yükleniyor…',
  linkUnavailable: 'Bağlantı kullanılamıyor',
  missingCheckoutLink: 'Ödeme bağlantısı eksik.',
  invalidCheckoutLink: 'Bu ödeme bağlantısı geçersiz veya süresi dolmuş.',
  cardNumberRequired: 'Kart numarası gereklidir',
  cardNumberInvalid: 'Geçerli bir kart numarası girin',
  nameRequired: 'Ad gereklidir',
  required: 'Zorunlu',
  expiryFormat: 'AA/YY',
  cvvInvalid: 'Geçersiz',
  paymentFailed: 'Ödeme başarısız. Kart bilgilerinizi kontrol edip tekrar deneyin.',
  redirectCard: (label) => `${label} ödemenizi güvenle tamamlamak için yönlendirileceksiniz.`,
  cardLabel: 'kart',
  paymentLabel: 'ödeme',
  verifyPayment: 'Ödemenizi doğrulayın',
  otpHint: 'Bu ödemeyi tamamlamak için telefonunuza gönderilen tek kullanımlık kodu girin.',
  otpLabel: 'Tek kullanımlık kod',
  verifying: 'Doğrulanıyor…',
  verifyAndPay: 'Doğrula ve öde',
  otpFailed: 'Kod doğrulaması başarısız. Farklı bir kartla tekrar deneyin.',
  refresh: 'Yenile',
  refreshing: 'Yenileniyor…',
  refreshHint: 'Ödeme durumunu yenile',
  refreshFailed: 'Ödeme durumu yenilenemedi. Lütfen tekrar deneyin.',
  submitted: 'Gönderildi',
  cryptoOnchainHint: 'Kripto göndermek için kendi cüzdanınızdan kullanabileceğiniz bir yatırma adresi alacaksınız.',
  cryptoOffchainHint:
    'Kripto ile ödemenizi tamamlamak için yönlendirileceksiniz. Cihazınızda yüklüyse KuCoin uygulaması da açılabilir.',
  currency: 'Para birimi',
  network: 'Ağ',
  loading: 'Yükleniyor…',
  redirecting: 'Yönlendiriliyor…',
  getDepositAddress: 'Yatırma adresini al',
  continueWithCrypto: 'Kripto ile devam et',
  continue: 'Devam',
  boltOnHint: 'Alternatif bir ödeme yöntemiyle tamamlamak için yönlendirileceksiniz.',
  cryptoStartFailed: 'Kripto ödemesi başlatılamadı. Lütfen tekrar deneyin.',
  paymentStartFailed: 'Ödeme başlatılamadı. Lütfen tekrar deneyin.',
  scanToPay: 'Ödemek için tarayın',
  sendExactly: 'Tam olarak gönderin',
  copied: 'Kopyalandı!',
  copyAddress: 'Adresi kopyala',
  expiresIn: (countdown) => `${countdown} içinde sona erer`,
  depositExpired: 'Bu yatırma adresinin süresi doldu — tekrar denemek için yenileyin.',
  overpayment: (received, currency) =>
    `Fazla ödeme alındı: ${received} ${currency}. Ödemeniz tamamlanıyor…`,
  wrongPayment: (received, currency) =>
    `Beklenmeyen ödeme alındı: ${received} ${currency}. İstenen para birimi/ağ ile eşleşmiyor — başka bir şey göndermeden destek ile iletişime geçin.`,
  underpayment: (received, currency) => `Eksik ödeme alındı: ${received} ${currency}.`,
  sendRemainingExact: (remaining, currency) =>
    `Bu ödemeyi tamamlamak için aynı adrese ${remaining} ${currency} daha gönderin.`,
  sendRemainingGeneric: 'Bu ödemeyi tamamlamak için kalan bakiyeyi aynı adrese gönderin.',
  waitingForPayment: 'Ödeme bekleniyor…',
  statusSuccessBadge: 'Ödeme başarılı',
  statusSuccessTitle: 'Ödeme başarılı',
  statusSuccessMessage: 'Ödemeniz başarıyla tamamlandı.',
  statusErrorBadge: 'Ödeme başarısız',
  statusErrorTitle: 'Ödeme başarısız',
  statusErrorMessage: 'Ödemenizi tamamlayamadık. Aşağıdaki işlem ayrıntılarını inceleyin.',
  statusPendingBadge: 'Ödeme işleniyor',
  statusPendingTitle: 'Ödemeniz işleniyor',
  statusPendingMessage: 'Ödemenizi güvenli şekilde işliyoruz. Bu genellikle birkaç saniye sürer.',
  redirectWait: 'Yönlendirilirken lütfen bekleyin…',
  redirectIn: (seconds) => `${seconds} saniye içinde yönlendirileceksiniz.`,
  transactionOverview: 'İşlem özeti',
  paymentDetails: 'Ödeme ayrıntıları',
  secure: 'Güvenli',
  transactionId: 'İşlem kimliği',
  amount: 'Tutar',
  status: 'Durum',
  reason: 'Neden',
  copy: 'Kopyala',
  copiedShort: 'Kopyalandı',
};

const RU: CheckoutCopy = {
  secureCheckout: 'Безопасная оплата',
  amountDue: 'Сумма к оплате',
  toMerchant: (name) => `для ${name}`,
  paymentMethod: 'Способ оплаты',
  cardMethod: 'Кредитная / дебетовая карта',
  cryptoMethod: 'Криптовалюта',
  alternativePayment: 'Альтернативная оплата',
  bankMethod: 'Оплата через банк',
  chooseBank: 'Выберите банк',
  searchBank: 'Найдите свой банк',
  noBanksFound: 'Банк не найден.',
  bankListUnavailable: 'Не удалось загрузить список банков. Продолжите и выберите банк на следующей странице.',
  payWithBank: 'Перейти в банк',
  bankRedirectNote: 'Вы безопасно подтвердите перевод в своём банке.',
  footerNote: 'Платежи шифруются и обрабатываются безопасно',
  cancel: 'Отмена',
  orderDetails: 'Детали заказа',
  orderIdLabel: 'ID заказа',
  amountLabel: 'Сумма',
  securePayments: 'Безопасные платежи',
  securityCode: 'Код безопасности',
  creditCard: 'Кредитная карта',
  cvvHint: '3-значный код на обратной стороне карты. У American Express 4 цифры на лицевой стороне.',
  noOrderDetails: 'Дополнительных сведений об этом платеже нет.',
  cardNumber: 'Номер карты',
  cardholderName: 'Имя владельца карты',
  expiryDate: 'Срок действия',
  cvv: 'CVV',
  cardNumberPlaceholder: '4242 4283 7159 2646',
  cardholderPlaceholder: 'Полное имя на карте',
  expiryPlaceholder: 'ММ/ГГ',
  cvvPlaceholder: 'CVV',
  otpPlaceholder: '123456',
  payNow: 'Оплатить сейчас',
  payAmount: (amount, currency) => `Оплатить ${amount} ${currency}`,
  processing: 'Обработка…',
  loadingDetails: 'Загрузка данных платежа…',
  linkUnavailable: 'Ссылка недоступна',
  missingCheckoutLink: 'Отсутствует ссылка для оплаты.',
  invalidCheckoutLink: 'Эта ссылка для оплаты недействительна или истекла.',
  cardNumberRequired: 'Необходимо указать номер карты',
  cardNumberInvalid: 'Введите действительный номер карты',
  nameRequired: 'Необходимо указать имя',
  required: 'Обязательно',
  expiryFormat: 'ММ/ГГ',
  cvvInvalid: 'Недействительно',
  paymentFailed: 'Платёж не выполнен. Проверьте данные карты и попробуйте снова.',
  redirectCard: (label) => `Вы будете перенаправлены, чтобы безопасно завершить оплату ${label}.`,
  cardLabel: 'картой',
  paymentLabel: 'платежа',
  verifyPayment: 'Подтвердите платёж',
  otpHint: 'Введите одноразовый код, отправленный на телефон, чтобы завершить этот платёж.',
  otpLabel: 'Одноразовый код',
  verifying: 'Проверка…',
  verifyAndPay: 'Подтвердить и оплатить',
  otpFailed: 'Проверка кода не удалась. Попробуйте снова с другой картой.',
  refresh: 'Обновить',
  refreshing: 'Обновление…',
  refreshHint: 'Обновить статус платежа',
  refreshFailed: 'Не удалось обновить статус платежа. Попробуйте снова.',
  submitted: 'Отправлено',
  cryptoOnchainHint: 'Вы получите адрес депозита, чтобы отправить криптовалюту напрямую со своего кошелька.',
  cryptoOffchainHint:
    'Вы будете перенаправлены, чтобы завершить оплату криптовалютой. Также может открыться приложение KuCoin, если оно установлено на устройстве.',
  currency: 'Валюта',
  network: 'Сеть',
  loading: 'Загрузка…',
  redirecting: 'Перенаправление…',
  getDepositAddress: 'Получить адрес депозита',
  continueWithCrypto: 'Продолжить с криптовалютой',
  continue: 'Продолжить',
  boltOnHint: 'Вы будете перенаправлены, чтобы завершить оплату альтернативным способом.',
  cryptoStartFailed: 'Не удалось начать криптоплатёж. Попробуйте снова.',
  paymentStartFailed: 'Не удалось начать платёж. Попробуйте снова.',
  scanToPay: 'Отсканируйте для оплаты',
  sendExactly: 'Отправьте ровно',
  copied: 'Скопировано!',
  copyAddress: 'Скопировать адрес',
  expiresIn: (countdown) => `Истекает через ${countdown}`,
  depositExpired: 'Срок действия этого адреса депозита истёк — обновите страницу и попробуйте снова.',
  overpayment: (received, currency) =>
    `Получена переплата: ${received} ${currency}. Завершаем платёж…`,
  wrongPayment: (received, currency) =>
    `Получен неожиданный платёж: ${received} ${currency}. Это не соответствует запрошенной валюте/сети — свяжитесь с поддержкой, прежде чем отправлять что-либо ещё.`,
  underpayment: (received, currency) => `Получена недоплата: ${received} ${currency}.`,
  sendRemainingExact: (remaining, currency) =>
    `Отправьте ещё ${remaining} ${currency} на тот же адрес, чтобы завершить этот платёж.`,
  sendRemainingGeneric: 'Отправьте оставшуюся сумму на тот же адрес, чтобы завершить этот платёж.',
  waitingForPayment: 'Ожидание платежа…',
  statusSuccessBadge: 'Платёж выполнен',
  statusSuccessTitle: 'Платёж выполнен',
  statusSuccessMessage: 'Ваш платёж успешно завершён.',
  statusErrorBadge: 'Платёж не выполнен',
  statusErrorTitle: 'Платёж не выполнен',
  statusErrorMessage: 'Не удалось завершить платёж. Проверьте сведения о транзакции ниже.',
  statusPendingBadge: 'Платёж обрабатывается',
  statusPendingTitle: 'Ваш платёж обрабатывается',
  statusPendingMessage: 'Мы безопасно обрабатываем ваш платёж. Обычно это занимает несколько секунд.',
  redirectWait: 'Подождите, выполняется перенаправление…',
  redirectIn: (seconds) => `Вы будете перенаправлены через ${seconds} с.`,
  transactionOverview: 'Обзор транзакции',
  paymentDetails: 'Сведения о платеже',
  secure: 'Безопасно',
  transactionId: 'ID транзакции',
  amount: 'Сумма',
  status: 'Статус',
  reason: 'Причина',
  copy: 'Копировать',
  copiedShort: 'Скопировано',
};

const AR: CheckoutCopy = {
  secureCheckout: 'دفع آمن',
  amountDue: 'المبلغ المستحق',
  toMerchant: (name) => `إلى ${name}`,
  paymentMethod: 'طريقة الدفع',
  cardMethod: 'بطاقة ائتمان / خصم',
  cryptoMethod: 'عملة مشفرة',
  alternativePayment: 'دفع بديل',
  bankMethod: 'الدفع عبر البنك',
  chooseBank: 'اختر بنكك',
  searchBank: 'ابحث عن بنكك',
  noBanksFound: 'لا يوجد بنك يطابق بحثك.',
  bankListUnavailable: 'تعذّر تحميل قائمة البنوك. تابع واختر بنكك في الصفحة التالية.',
  payWithBank: 'المتابعة إلى بنكك',
  bankRedirectNote: 'ستؤكد التحويل بأمان في بنكك.',
  footerNote: 'يتم تشفير المدفوعات ومعالجتها بأمان',
  cancel: 'إلغاء',
  orderDetails: 'تفاصيل الطلب',
  orderIdLabel: 'معرّف الطلب',
  amountLabel: 'المبلغ',
  securePayments: 'مدفوعات آمنة',
  securityCode: 'رمز الأمان',
  creditCard: 'بطاقة ائتمان',
  cvvHint: 'رمز من 3 أرقام على ظهر البطاقة. تستخدم American Express 4 أرقام على الوجه.',
  noOrderDetails: 'لا توجد تفاصيل إضافية لهذه الدفعة.',
  cardNumber: 'رقم البطاقة',
  cardholderName: 'اسم حامل البطاقة',
  expiryDate: 'تاريخ الانتهاء',
  cvv: 'CVV',
  cardNumberPlaceholder: '4242 4283 7159 2646',
  cardholderPlaceholder: 'الاسم الكامل على البطاقة',
  expiryPlaceholder: 'MM/YY',
  cvvPlaceholder: 'CVV',
  otpPlaceholder: '123456',
  payNow: 'ادفع الآن',
  payAmount: (amount, currency) => `ادفع ${amount} ${currency}`,
  processing: 'جارٍ المعالجة…',
  loadingDetails: 'جارٍ تحميل تفاصيل الدفع…',
  linkUnavailable: 'الرابط غير متاح',
  missingCheckoutLink: 'رابط الدفع مفقود.',
  invalidCheckoutLink: 'رابط الدفع هذا غير صالح أو منتهي الصلاحية.',
  cardNumberRequired: 'رقم البطاقة مطلوب',
  cardNumberInvalid: 'أدخل رقم بطاقة صالحًا',
  nameRequired: 'الاسم مطلوب',
  required: 'مطلوب',
  expiryFormat: 'MM/YY',
  cvvInvalid: 'غير صالح',
  paymentFailed: 'فشل الدفع. يرجى التحقق من بيانات البطاقة والمحاولة مرة أخرى.',
  redirectCard: (label) => `سيتم توجيهك لإكمال دفع ${label} بأمان.`,
  cardLabel: 'البطاقة',
  paymentLabel: 'الدفع',
  verifyPayment: 'تحقق من الدفع',
  otpHint: 'أدخل الرمز لمرة واحدة المرسل إلى هاتفك لإكمال هذا الدفع.',
  otpLabel: 'رمز لمرة واحدة',
  verifying: 'جارٍ التحقق…',
  verifyAndPay: 'تحقق وادفع',
  otpFailed: 'فشل التحقق من الرمز. حاول مرة أخرى ببطاقة مختلفة.',
  refresh: 'تحديث',
  refreshing: 'جارٍ التحديث…',
  refreshHint: 'تحديث حالة الدفع',
  refreshFailed: 'تعذر تحديث حالة الدفع. يرجى المحاولة مرة أخرى.',
  submitted: 'تم الإرسال',
  cryptoOnchainHint: 'ستحصل على عنوان إيداع لإرسال العملة المشفرة مباشرة من محفظتك.',
  cryptoOffchainHint:
    'سيتم توجيهك لإكمال الدفع عبر العملة المشفرة. قد يُفتح أيضًا تطبيق KuCoin إذا كان مثبتًا على جهازك.',
  currency: 'العملة',
  network: 'الشبكة',
  loading: 'جارٍ التحميل…',
  redirecting: 'جارٍ التوجيه…',
  getDepositAddress: 'الحصول على عنوان الإيداع',
  continueWithCrypto: 'المتابعة بالعملة المشفرة',
  continue: 'متابعة',
  boltOnHint: 'سيتم توجيهك لإكمال الدفع عبر طريقة بديلة.',
  cryptoStartFailed: 'تعذر بدء دفع العملة المشفرة. يرجى المحاولة مرة أخرى.',
  paymentStartFailed: 'تعذر بدء الدفع. يرجى المحاولة مرة أخرى.',
  scanToPay: 'امسح للدفع',
  sendExactly: 'أرسل بالضبط',
  copied: 'تم النسخ!',
  copyAddress: 'نسخ العنوان',
  expiresIn: (countdown) => `ينتهي خلال ${countdown}`,
  depositExpired: 'انتهت صلاحية عنوان الإيداع هذا — حدّث الصفحة للمحاولة مرة أخرى.',
  overpayment: (received, currency) =>
    `تم استلام دفعة زائدة: ${received} ${currency}. جارٍ إكمال الدفع…`,
  wrongPayment: (received, currency) =>
    `تم استلام دفعة غير متوقعة: ${received} ${currency}. هذا لا يطابق العملة/الشبكة المطلوبة — يرجى التواصل مع الدعم قبل إرسال أي شيء آخر.`,
  underpayment: (received, currency) => `تم استلام دفعة ناقصة: ${received} ${currency}.`,
  sendRemainingExact: (remaining, currency) =>
    `يرجى إرسال ${remaining} ${currency} إضافية إلى نفس العنوان لإكمال هذا الدفع.`,
  sendRemainingGeneric: 'يرجى إرسال الرصيد المتبقي إلى نفس العنوان لإكمال هذا الدفع.',
  waitingForPayment: 'في انتظار الدفع…',
  statusSuccessBadge: 'تم الدفع بنجاح',
  statusSuccessTitle: 'تم الدفع بنجاح',
  statusSuccessMessage: 'اكتمل دفعك بنجاح.',
  statusErrorBadge: 'فشل الدفع',
  statusErrorTitle: 'فشل الدفع',
  statusErrorMessage: 'تعذر إكمال الدفع. يرجى مراجعة تفاصيل المعاملة أدناه.',
  statusPendingBadge: 'جارٍ معالجة الدفع',
  statusPendingTitle: 'جارٍ معالجة دفعك',
  statusPendingMessage: 'نعالج دفعك بأمان. عادةً ما يستغرق ذلك لحظات قليلة.',
  redirectWait: 'يرجى الانتظار ريثما نقوم بتوجيهك…',
  redirectIn: (seconds) => `سيتم توجيهك خلال ${seconds} ثانية.`,
  transactionOverview: 'نظرة عامة على المعاملة',
  paymentDetails: 'تفاصيل الدفع',
  secure: 'آمن',
  transactionId: 'معرّف المعاملة',
  amount: 'المبلغ',
  status: 'الحالة',
  reason: 'السبب',
  copy: 'نسخ',
  copiedShort: 'تم النسخ',
};

const ZH: CheckoutCopy = {
  secureCheckout: '安全结账',
  amountDue: '应付金额',
  toMerchant: (name) => `支付给 ${name}`,
  paymentMethod: '支付方式',
  cardMethod: '信用卡 / 借记卡',
  cryptoMethod: '加密货币',
  alternativePayment: '其他支付方式',
  bankMethod: '通过银行付款',
  chooseBank: '选择您的银行',
  searchBank: '搜索您的银行',
  noBanksFound: '没有与搜索匹配的银行。',
  bankListUnavailable: '无法加载银行列表。请继续，并在下一页选择您的银行。',
  payWithBank: '前往您的银行',
  bankRedirectNote: '您将在自己的银行中安全地确认转账。',
  footerNote: '付款已加密并安全处理',
  cancel: '取消',
  orderDetails: '订单详情',
  orderIdLabel: '订单 ID',
  amountLabel: '金额',
  securePayments: '安全支付',
  securityCode: '安全码',
  creditCard: '信用卡',
  cvvHint: '卡背面的 3 位安全码。美国运通卡使用正面的 4 位安全码。',
  noOrderDetails: '此付款没有更多订单详情。',
  cardNumber: '卡号',
  cardholderName: '持卡人姓名',
  expiryDate: '有效期',
  cvv: 'CVV',
  cardNumberPlaceholder: '4242 4283 7159 2646',
  cardholderPlaceholder: '卡上的全名',
  expiryPlaceholder: '月/年',
  cvvPlaceholder: 'CVV',
  otpPlaceholder: '123456',
  payNow: '立即支付',
  payAmount: (amount, currency) => `支付 ${amount} ${currency}`,
  processing: '处理中…',
  loadingDetails: '正在加载付款信息…',
  linkUnavailable: '链接不可用',
  missingCheckoutLink: '缺少结账链接。',
  invalidCheckoutLink: '此结账链接无效或已过期。',
  cardNumberRequired: '请输入卡号',
  cardNumberInvalid: '请输入有效卡号',
  nameRequired: '请输入姓名',
  required: '必填',
  expiryFormat: 'MM/YY',
  cvvInvalid: '无效',
  paymentFailed: '支付失败。请检查您的卡片信息后重试。',
  redirectCard: (label) => `即将跳转以安全完成您的${label}付款。`,
  cardLabel: '卡片',
  paymentLabel: '付款',
  verifyPayment: '验证您的付款',
  otpHint: '请输入发送到手机的一次性验证码以完成本次付款。',
  otpLabel: '一次性验证码',
  verifying: '验证中…',
  verifyAndPay: '验证并支付',
  otpFailed: '验证码验证失败。请换一张卡重试。',
  refresh: '刷新',
  refreshing: '刷新中…',
  refreshHint: '刷新付款状态',
  refreshFailed: '无法刷新付款状态。请重试。',
  submitted: '已提交',
  cryptoOnchainHint: '您将获得一个充值地址，可从自己的钱包直接发送加密货币。',
  cryptoOffchainHint:
    '即将跳转以通过加密货币完成付款。如果设备已安装 KuCoin 应用，也可能会打开该应用。',
  currency: '币种',
  network: '网络',
  loading: '加载中…',
  redirecting: '跳转中…',
  getDepositAddress: '获取充值地址',
  continueWithCrypto: '使用加密货币继续',
  continue: '继续',
  boltOnHint: '即将跳转以通过其他支付方式完成付款。',
  cryptoStartFailed: '无法开始加密货币付款。请重试。',
  paymentStartFailed: '无法开始付款。请重试。',
  scanToPay: '扫码支付',
  sendExactly: '请准确发送',
  copied: '已复制！',
  copyAddress: '复制地址',
  expiresIn: (countdown) => `${countdown} 后过期`,
  depositExpired: '此充值地址已过期 — 请刷新后重试。',
  overpayment: (received, currency) =>
    `收到超额付款：${received} ${currency}。正在完成付款…`,
  wrongPayment: (received, currency) =>
    `收到意外付款：${received} ${currency}。与请求的币种/网络不符 — 请在继续发送前联系客服。`,
  underpayment: (received, currency) => `收到不足付款：${received} ${currency}。`,
  sendRemainingExact: (remaining, currency) =>
    `请再向同一地址发送 ${remaining} ${currency} 以完成本次付款。`,
  sendRemainingGeneric: '请将剩余金额发送到同一地址以完成本次付款。',
  waitingForPayment: '等待付款中…',
  statusSuccessBadge: '付款成功',
  statusSuccessTitle: '付款成功',
  statusSuccessMessage: '您的付款已成功完成。',
  statusErrorBadge: '付款失败',
  statusErrorTitle: '付款失败',
  statusErrorMessage: '我们无法完成您的付款。请查看下方交易详情。',
  statusPendingBadge: '付款处理中',
  statusPendingTitle: '正在处理您的付款',
  statusPendingMessage: '我们正在安全处理您的付款，通常只需片刻。',
  redirectWait: '正在跳转，请稍候…',
  redirectIn: (seconds) => `${seconds} 秒后跳转。`,
  transactionOverview: '交易概览',
  paymentDetails: '付款详情',
  secure: '安全',
  transactionId: '交易 ID',
  amount: '金额',
  status: '状态',
  reason: '原因',
  copy: '复制',
  copiedShort: '已复制',
};

const JA: CheckoutCopy = {
  secureCheckout: '安全なお支払い',
  amountDue: 'お支払い金額',
  toMerchant: (name) => `${name} へ`,
  paymentMethod: 'お支払い方法',
  cardMethod: 'クレジットカード / デビットカード',
  cryptoMethod: '暗号資産',
  alternativePayment: 'その他のお支払い',
  bankMethod: '銀行で支払う',
  chooseBank: '銀行を選択',
  searchBank: '銀行を検索',
  noBanksFound: '該当する銀行がありません。',
  bankListUnavailable: '銀行一覧を読み込めませんでした。次のページで銀行を選択してください。',
  payWithBank: '銀行へ進む',
  bankRedirectNote: 'ご自身の銀行で振込を安全に確認します。',
  footerNote: 'お支払いは暗号化され、安全に処理されます',
  cancel: 'キャンセル',
  orderDetails: '注文の詳細',
  orderIdLabel: '注文ID',
  amountLabel: '金額',
  securePayments: '安全な支払い',
  securityCode: 'セキュリティコード',
  creditCard: 'クレジットカード',
  cvvHint: 'カード裏面の3桁のコードです。American Expressは表面の4桁です。',
  noOrderDetails: 'この支払いの追加情報はありません。',
  cardNumber: 'カード番号',
  cardholderName: 'カード名義人',
  expiryDate: '有効期限',
  cvv: 'セキュリティコード',
  cardNumberPlaceholder: '4242 4283 7159 2646',
  cardholderPlaceholder: 'カード上の氏名',
  expiryPlaceholder: '月/年',
  cvvPlaceholder: 'CVV',
  otpPlaceholder: '123456',
  payNow: '今すぐ支払う',
  payAmount: (amount, currency) => `${amount} ${currency} を支払う`,
  processing: '処理中…',
  loadingDetails: 'お支払い情報を読み込み中…',
  linkUnavailable: 'リンクが無効です',
  missingCheckoutLink: 'チェックアウトリンクがありません。',
  invalidCheckoutLink: 'このチェックアウトリンクは無効か、有効期限が切れています。',
  cardNumberRequired: 'カード番号を入力してください',
  cardNumberInvalid: '有効なカード番号を入力してください',
  nameRequired: '名義人を入力してください',
  required: '必須',
  expiryFormat: 'MM/YY',
  cvvInvalid: '無効です',
  paymentFailed: 'お支払いに失敗しました。カード情報をご確認のうえ、もう一度お試しください。',
  redirectCard: (label) => `安全にお支払いを完了するため、${label}のお支払いページへ移動します。`,
  cardLabel: 'カード',
  paymentLabel: 'お支払い',
  verifyPayment: 'お支払いの確認',
  otpHint: 'お支払いを完了するには、携帯電話に送信されたワンタイムコードを入力してください。',
  otpLabel: 'ワンタイムコード',
  verifying: '確認中…',
  verifyAndPay: '確認して支払う',
  otpFailed: 'ワンタイムコードの確認に失敗しました。別のカードでもう一度お試しください。',
  refresh: '更新',
  refreshing: '更新中…',
  refreshHint: '支払い状況を更新',
  refreshFailed: '支払い状況を更新できませんでした。もう一度お試しください。',
  submitted: '送信済み',
  cryptoOnchainHint: 'ご自身のウォレットから暗号資産を送金するための入金アドレスが表示されます。',
  cryptoOffchainHint:
    '暗号資産でのお支払いを完了するため、別のページへ移動します。端末にKuCoinアプリがインストールされている場合は、アプリが起動することがあります。',
  currency: '通貨',
  network: 'ネットワーク',
  loading: '読み込み中…',
  redirecting: '移動中…',
  getDepositAddress: '入金アドレスを取得',
  continueWithCrypto: '暗号資産で続ける',
  continue: '続ける',
  boltOnHint: '別のお支払い方法で完了するため、ページへ移動します。',
  cryptoStartFailed: '暗号資産でのお支払いを開始できませんでした。もう一度お試しください。',
  paymentStartFailed: 'お支払いを開始できませんでした。もう一度お試しください。',
  scanToPay: 'スキャンしてお支払い',
  sendExactly: '次の金額を正確に送金してください',
  copied: 'コピーしました',
  copyAddress: 'アドレスをコピー',
  expiresIn: (countdown) => `有効期限まで ${countdown}`,
  depositExpired: 'この入金アドレスの有効期限が切れました。ページを更新してもう一度お試しください。',
  overpayment: (received, currency) =>
    `過入金を受け付けました: ${received} ${currency}。お支払いを完了しています…`,
  wrongPayment: (received, currency) =>
    `予期しない入金を受け付けました: ${received} ${currency}。ご指定の通貨/ネットワークと一致しません。追加の送金はせず、サポートまでご連絡ください。`,
  underpayment: (received, currency) => `入金額が不足しています: ${received} ${currency}。`,
  sendRemainingExact: (remaining, currency) =>
    `同じアドレスにさらに ${remaining} ${currency} をお送りください。`,
  sendRemainingGeneric: '同じアドレスに残額をお送りください。',
  waitingForPayment: '入金を待っています…',
  statusSuccessBadge: 'お支払いが完了しました',
  statusSuccessTitle: 'お支払いが完了しました',
  statusSuccessMessage: 'お支払いが正常に完了しました。',
  statusErrorBadge: 'お支払いに失敗しました',
  statusErrorTitle: 'お支払いに失敗しました',
  statusErrorMessage: 'お支払いを完了できませんでした。下記の取引内容をご確認ください。',
  statusPendingBadge: 'お支払いを処理中',
  statusPendingTitle: 'お支払いを処理しています',
  statusPendingMessage: 'お支払いを安全に処理しています。通常、数秒で完了します。',
  redirectWait: 'リダイレクトしています。しばらくお待ちください…',
  redirectIn: (seconds) => `${seconds}秒後に移動します。`,
  transactionOverview: '取引概要',
  paymentDetails: 'お支払い詳細',
  secure: '安全',
  transactionId: '取引ID',
  amount: '金額',
  status: 'ステータス',
  reason: '理由',
  copy: 'コピー',
  copiedShort: 'コピー済み',
};

const KO: CheckoutCopy = {
  secureCheckout: '안전한 결제',
  amountDue: '결제 금액',
  toMerchant: (name) => `${name}에게`,
  paymentMethod: '결제 수단',
  cardMethod: '신용 / 체크 카드',
  cryptoMethod: '암호화폐',
  alternativePayment: '기타 결제',
  bankMethod: '은행으로 결제',
  chooseBank: '은행 선택',
  searchBank: '은행 검색',
  noBanksFound: '검색과 일치하는 은행이 없습니다.',
  bankListUnavailable: '은행 목록을 불러오지 못했습니다. 계속 진행하여 다음 페이지에서 은행을 선택하세요.',
  payWithBank: '은행으로 이동',
  bankRedirectNote: '본인 은행에서 이체를 안전하게 확인합니다.',
  footerNote: '결제는 암호화되어 안전하게 처리됩니다',
  cancel: '취소',
  orderDetails: '주문 상세',
  orderIdLabel: '주문 ID',
  amountLabel: '금액',
  securePayments: '보안 결제',
  securityCode: '보안 코드',
  creditCard: '신용카드',
  cvvHint: '카드 뒷면의 3자리 코드입니다. American Express는 앞면의 4자리 코드를 사용합니다.',
  noOrderDetails: '이 결제에 대한 추가 주문 정보가 없습니다.',
  cardNumber: '카드 번호',
  cardholderName: '카드 소유자 이름',
  expiryDate: '유효기간',
  cvv: 'CVV',
  cardNumberPlaceholder: '4242 4283 7159 2646',
  cardholderPlaceholder: '카드에 적힌 성함',
  expiryPlaceholder: '월/년',
  cvvPlaceholder: 'CVV',
  otpPlaceholder: '123456',
  payNow: '지금 결제',
  payAmount: (amount, currency) => `${amount} ${currency} 결제`,
  processing: '처리 중…',
  loadingDetails: '결제 정보를 불러오는 중…',
  linkUnavailable: '링크를 사용할 수 없음',
  missingCheckoutLink: '결제 링크가 없습니다.',
  invalidCheckoutLink: '이 결제 링크는 유효하지 않거나 만료되었습니다.',
  cardNumberRequired: '카드 번호를 입력하세요',
  cardNumberInvalid: '유효한 카드 번호를 입력하세요',
  nameRequired: '이름을 입력하세요',
  required: '필수',
  expiryFormat: 'MM/YY',
  cvvInvalid: '유효하지 않음',
  paymentFailed: '결제에 실패했습니다. 카드 정보를 확인한 후 다시 시도하세요.',
  redirectCard: (label) => `${label} 결제를 안전하게 완료하기 위해 이동합니다.`,
  cardLabel: '카드',
  paymentLabel: '결제',
  verifyPayment: '결제를 확인하세요',
  otpHint: '이 결제를 완료하려면 휴대폰으로 전송된 일회용 코드를 입력하세요.',
  otpLabel: '일회용 코드',
  verifying: '확인 중…',
  verifyAndPay: '확인하고 결제',
  otpFailed: '코드 확인에 실패했습니다. 다른 카드로 다시 시도하세요.',
  refresh: '새로고침',
  refreshing: '새로고침 중…',
  refreshHint: '결제 상태 새로고침',
  refreshFailed: '결제 상태를 새로고침할 수 없습니다. 다시 시도하세요.',
  submitted: '제출됨',
  cryptoOnchainHint: '본인 지갑에서 암호화폐를 보낼 입금 주소가 표시됩니다.',
  cryptoOffchainHint:
    '암호화폐로 결제를 완료하기 위해 이동합니다. 기기에 KuCoin 앱이 설치되어 있으면 앱이 열릴 수도 있습니다.',
  currency: '통화',
  network: '네트워크',
  loading: '불러오는 중…',
  redirecting: '이동 중…',
  getDepositAddress: '입금 주소 받기',
  continueWithCrypto: '암호화폐로 계속',
  continue: '계속',
  boltOnHint: '다른 결제 수단으로 완료하기 위해 이동합니다.',
  cryptoStartFailed: '암호화폐 결제를 시작할 수 없습니다. 다시 시도하세요.',
  paymentStartFailed: '결제를 시작할 수 없습니다. 다시 시도하세요.',
  scanToPay: '스캔하여 결제',
  sendExactly: '정확히 보내기',
  copied: '복사됨!',
  copyAddress: '주소 복사',
  expiresIn: (countdown) => `${countdown} 후 만료`,
  depositExpired: '이 입금 주소가 만료되었습니다. 새로고침 후 다시 시도하세요.',
  overpayment: (received, currency) =>
    `초과 입금이 확인되었습니다: ${received} ${currency}. 결제를 완료하는 중…`,
  wrongPayment: (received, currency) =>
    `예상치 못한 입금이 확인되었습니다: ${received} ${currency}. 요청한 통화/네트워크와 일치하지 않습니다. 추가 송금 전에 지원팀에 문의하세요.`,
  underpayment: (received, currency) => `입금이 부족합니다: ${received} ${currency}.`,
  sendRemainingExact: (remaining, currency) =>
    `이 결제를 완료하려면 같은 주소로 ${remaining} ${currency}를 더 보내 주세요.`,
  sendRemainingGeneric: '이 결제를 완료하려면 남은 금액을 같은 주소로 보내 주세요.',
  waitingForPayment: '결제를 기다리는 중…',
  statusSuccessBadge: '결제 완료',
  statusSuccessTitle: '결제 완료',
  statusSuccessMessage: '결제가 성공적으로 완료되었습니다.',
  statusErrorBadge: '결제 실패',
  statusErrorTitle: '결제 실패',
  statusErrorMessage: '결제를 완료할 수 없습니다. 아래 거래 내역을 확인해 주세요.',
  statusPendingBadge: '결제 처리 중',
  statusPendingTitle: '결제를 처리하고 있습니다',
  statusPendingMessage: '결제를 안전하게 처리하고 있습니다. 보통 잠시 후에 완료됩니다.',
  redirectWait: '이동하는 동안 잠시 기다려 주세요…',
  redirectIn: (seconds) => `${seconds}초 후에 이동합니다.`,
  transactionOverview: '거래 개요',
  paymentDetails: '결제 세부정보',
  secure: '보안',
  transactionId: '거래 ID',
  amount: '금액',
  status: '상태',
  reason: '사유',
  copy: '복사',
  copiedShort: '복사됨',
};

const HI: CheckoutCopy = {
  secureCheckout: 'सुरक्षित भुगतान',
  amountDue: 'देय राशि',
  toMerchant: (name) => `${name} को`,
  paymentMethod: 'भुगतान विधि',
  cardMethod: 'क्रेडिट / डेबिट कार्ड',
  cryptoMethod: 'क्रिप्टोकरेंसी',
  alternativePayment: 'वैकल्पिक भुगतान',
  bankMethod: 'बैंक से भुगतान करें',
  chooseBank: 'अपना बैंक चुनें',
  searchBank: 'अपना बैंक खोजें',
  noBanksFound: 'आपकी खोज से कोई बैंक मेल नहीं खाता।',
  bankListUnavailable: 'बैंकों की सूची लोड नहीं हो सकी। आगे बढ़ें और अगले पेज पर अपना बैंक चुनें।',
  payWithBank: 'अपने बैंक पर जाएँ',
  bankRedirectNote: 'आप अपने बैंक में सुरक्षित रूप से ट्रांसफ़र की पुष्टि करेंगे।',
  footerNote: 'भुगतान एन्क्रिप्टेड हैं और सुरक्षित रूप से प्रोसेस किए जाते हैं',
  cancel: 'रद्द करें',
  orderDetails: 'ऑर्डर विवरण',
  orderIdLabel: 'ऑर्डर ID',
  amountLabel: 'राशि',
  securePayments: 'सुरक्षित भुगतान',
  securityCode: 'सुरक्षा कोड',
  creditCard: 'क्रेडिट कार्ड',
  cvvHint: 'कार्ड के पीछे का 3 अंकों का कोड। American Express सामने 4 अंकों का कोड इस्तेमाल करता है।',
  noOrderDetails: 'इस भुगतान के लिए अतिरिक्त विवरण नहीं है।',
  cardNumber: 'कार्ड नंबर',
  cardholderName: 'कार्डधारक का नाम',
  expiryDate: 'समाप्ति तिथि',
  cvv: 'CVV',
  cardNumberPlaceholder: '4242 4283 7159 2646',
  cardholderPlaceholder: 'कार्ड पर पूरा नाम',
  expiryPlaceholder: 'MM/YY',
  cvvPlaceholder: 'CVV',
  otpPlaceholder: '123456',
  payNow: 'अभी भुगतान करें',
  payAmount: (amount, currency) => `${amount} ${currency} का भुगतान करें`,
  processing: 'प्रोसेस हो रहा है…',
  loadingDetails: 'भुगतान विवरण लोड हो रहा है…',
  linkUnavailable: 'लिंक उपलब्ध नहीं है',
  missingCheckoutLink: 'चेकआउट लिंक नहीं मिला।',
  invalidCheckoutLink: 'यह चेकआउट लिंक अमान्य है या समाप्त हो गया है।',
  cardNumberRequired: 'कार्ड नंबर आवश्यक है',
  cardNumberInvalid: 'एक मान्य कार्ड नंबर दर्ज करें',
  nameRequired: 'नाम आवश्यक है',
  required: 'आवश्यक',
  expiryFormat: 'MM/YY',
  cvvInvalid: 'अमान्य',
  paymentFailed: 'भुगतान विफल रहा। कृपया अपने कार्ड विवरण जांचें और फिर कोशिश करें।',
  redirectCard: (label) => `अपना ${label} भुगतान सुरक्षित रूप से पूरा करने के लिए आपको रीडायरेक्ट किया जाएगा।`,
  cardLabel: 'कार्ड',
  paymentLabel: 'भुगतान',
  verifyPayment: 'अपना भुगतान सत्यापित करें',
  otpHint: 'यह भुगतान पूरा करने के लिए अपने फ़ोन पर भेजा गया वन-टाइम कोड दर्ज करें।',
  otpLabel: 'वन-टाइम कोड',
  verifying: 'सत्यापित हो रहा है…',
  verifyAndPay: 'सत्यापित करें और भुगतान करें',
  otpFailed: 'कोड सत्यापन विफल रहा। कृपया किसी अन्य कार्ड से फिर कोशिश करें।',
  refresh: 'रीफ़्रेश',
  refreshing: 'रीफ़्रेश हो रहा है…',
  refreshHint: 'भुगतान स्थिति रीफ़्रेश करें',
  refreshFailed: 'भुगतान स्थिति रीफ़्रेश नहीं हो सकी। कृपया फिर कोशिश करें।',
  submitted: 'सबमिट किया गया',
  cryptoOnchainHint: 'आपको अपने वॉलेट से क्रिप्टो भेजने के लिए एक डिपॉज़िट पता मिलेगा।',
  cryptoOffchainHint:
    'क्रिप्टो से भुगतान पूरा करने के लिए आपको रीडायरेक्ट किया जाएगा। यदि डिवाइस पर KuCoin ऐप इंस्टॉल है तो वह भी खुल सकता है।',
  currency: 'मुद्रा',
  network: 'नेटवर्क',
  loading: 'लोड हो रहा है…',
  redirecting: 'रीडायरेक्ट हो रहा है…',
  getDepositAddress: 'डिपॉज़िट पता प्राप्त करें',
  continueWithCrypto: 'क्रिप्टो के साथ जारी रखें',
  continue: 'जारी रखें',
  boltOnHint: 'वैकल्पिक भुगतान विधि से पूरा करने के लिए आपको रीडायरेक्ट किया जाएगा।',
  cryptoStartFailed: 'क्रिप्टो भुगतान शुरू नहीं हो सका। कृपया फिर कोशिश करें।',
  paymentStartFailed: 'भुगतान शुरू नहीं हो सका। कृपया फिर कोशिश करें।',
  scanToPay: 'भुगतान के लिए स्कैन करें',
  sendExactly: 'बिल्कुल इतना भेजें',
  copied: 'कॉपी हो गया!',
  copyAddress: 'पता कॉपी करें',
  expiresIn: (countdown) => `${countdown} में समाप्त`,
  depositExpired: 'यह डिपॉज़िट पता समाप्त हो गया है — फिर कोशिश करने के लिए रीफ़्रेश करें।',
  overpayment: (received, currency) =>
    `अधिक भुगतान प्राप्त हुआ: ${received} ${currency}। आपका भुगतान पूरा किया जा रहा है…`,
  wrongPayment: (received, currency) =>
    `अप्रत्याशित भुगतान प्राप्त हुआ: ${received} ${currency}। यह अनुरोधित मुद्रा/नेटवर्क से मेल नहीं खाता — कुछ और भेजने से पहले सपोर्ट से संपर्क करें।`,
  underpayment: (received, currency) => `कम भुगतान प्राप्त हुआ: ${received} ${currency}।`,
  sendRemainingExact: (remaining, currency) =>
    `यह भुगतान पूरा करने के लिए उसी पते पर ${remaining} ${currency} और भेजें।`,
  sendRemainingGeneric: 'यह भुगतान पूरा करने के लिए शेष राशि उसी पते पर भेजें।',
  waitingForPayment: 'भुगतान की प्रतीक्षा…',
  statusSuccessBadge: 'भुगतान सफल',
  statusSuccessTitle: 'भुगतान सफल',
  statusSuccessMessage: 'आपका भुगतान सफलतापूर्वक पूरा हो गया है।',
  statusErrorBadge: 'भुगतान विफल',
  statusErrorTitle: 'भुगतान विफल',
  statusErrorMessage: 'हम आपका भुगतान पूरा नहीं कर सके। कृपया नीचे लेन-देन विवरण देखें।',
  statusPendingBadge: 'भुगतान प्रोसेस हो रहा है',
  statusPendingTitle: 'आपका भुगतान प्रोसेस हो रहा है',
  statusPendingMessage: 'हम आपका भुगतान सुरक्षित रूप से प्रोसेस कर रहे हैं। इसमें आमतौर पर कुछ क्षण लगते हैं।',
  redirectWait: 'रीडायरेक्ट करते समय कृपया प्रतीक्षा करें…',
  redirectIn: (seconds) => `आपको ${seconds} सेकंड में रीडायरेक्ट किया जाएगा।`,
  transactionOverview: 'लेन-देन सारांश',
  paymentDetails: 'भुगतान विवरण',
  secure: 'सुरक्षित',
  transactionId: 'लेन-देन ID',
  amount: 'राशि',
  status: 'स्थिति',
  reason: 'कारण',
  copy: 'कॉपी',
  copiedShort: 'कॉपी किया',
};

const COPY: Record<CheckoutLocale, CheckoutCopy> = {
  en: EN,
  es: ES,
  fr: FR,
  de: DE,
  it: IT,
  pt: PT,
  nl: NL,
  pl: PL,
  tr: TR,
  ru: RU,
  ar: AR,
  zh: ZH,
  ja: JA,
  ko: KO,
  hi: HI,
};

export function getCheckoutCopy(locale: CheckoutLocale): CheckoutCopy {
  return COPY[locale] ?? EN;
}
